import { LinearGradient } from "expo-linear-gradient";
import { Stack, useLocalSearchParams, useNavigation } from "expo-router";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type UIEvent as ReactUIEvent
} from "react";
import {
  Animated,
  AppState,
  BackHandler,
  Easing,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
  type ViewStyle
} from "react-native";
import {
  KeyboardAwareScrollView,
  type KeyboardAwareScrollViewRef
} from "react-native-keyboard-controller";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import type {
  Dance,
  PlaybackMode,
  ResumeState
} from "@recital-master/shared";
import {
  danceStyleLabel,
  formatDuration,
  searchDances,
  spokenDeviceName
} from "@recital-master/shared";

import { useAuthState } from "@/auth/AuthProvider";
import { useAppRouter } from "@/lib/appRouter";
import { AppHeader } from "@/components/AppHeader";
import { Artwork, Backdrop } from "@/components/Artwork";
import { DANCE_ROW_HEIGHT, DanceRow } from "@/components/DanceRow";
import { DanceListHeader } from "@/components/DanceListHeader";
import { DeviceSwapPanel } from "@/components/DeviceSwapPanel";
import { HeaderBackdropScrim } from "@/components/HeaderBackdropScrim";
import { HeaderContentMask } from "@/components/HeaderContentMask";
import { PageBackdrop, usePageBackdrop } from "@/components/PageBackdrop";
import { PlayerVeil } from "@/components/PlayerVeil";
import { ScrollEdgeFade } from "@/components/ScrollEdgeFade";
import { SpecularEdge } from "@/components/SpecularEdge";
import { useAccount } from "@/data/AccountProvider";
import {
  useCatalog,
  useDances,
  useDevMediaUrl,
  useMediaUrl,
  usePerformance,
  usePerformanceDetailsStatus,
  usePerformanceFeaturedArtwork,
  usePerformances,
  useRecital,
  useRecitalArtwork
} from "@/data/CatalogProvider";
import { BRAND_BACKDROP_URI } from "@/design/brandBackdrop";
import { pageBackdropPresentation } from "@/design/backdropTone";
import { Button } from "@/design/Button";
import { getAppHeaderHeight } from "@/design/headerMetrics";
import { webTouchPlayerSurface } from "@/design/interactionMotion";
import { SEARCH_KEYBOARD_GAP } from "@/design/keyboardMetrics";
import { hintWebNavigationDirection } from "@/design/navigationMotionState";
import { APP_HEADER_CONTENT_GAP, Divider, useEnterTransition } from "@/design/primitives";
import {
  lockAppPortrait,
  lockPlayerLandscape
} from "@/design/screenOrientation";
import { Muted, Overline, Text } from "@/design/Text";
import {
  fonts,
  gradients,
  palette,
  radius,
  space
} from "@/design/tokens";
import { displayTitle, DOT_SEPARATOR, joinWithDot } from "@/lib/format";
import { performancePath, recitalPath } from "@/lib/routes";
import { AudioFeedChip } from "@/playback/AudioFeedChip";
import {
  acceptedReportedAudioFeedId,
  persistAudioFeedId,
  readPersistedAudioFeedId
} from "@/playback/audioFeedPreference";
import { PlayerChrome } from "@/playback/PlayerChrome";
import { DisplacedPlaybackPanel } from "@/playback/DisplacedPlaybackPanel";
import { usePlaybackSession } from "@/playback/PlaybackSession";
import { useAirPlayController } from "@/playback/airplay/AirPlayController";
import { AirPlayVideoPresentation } from "@/playback/airplay/AirPlayVideoPresentation";
import { useTerminatePlaybackOnAuthSessionChange } from "@/playback/authPlaybackLifecycle";
import { RemotePlaybackTitleBlock } from "@/playback/RemotePlaybackHeroChrome";
import { selectDisplacedPlayback } from "@/playback/displacedPlayback";
import { getCachedPlaybackThumbUrl } from "@/playback/playbackGrantCache";
import {
  playbackQueueOwnerKey,
  remotePlaybackQueueId,
  watchQueueInstanceKey
} from "@/playback/playbackQueueIdentity";
import { useCastController } from "@/playback/cast/CastController";
import type { CastPlaybackQueue } from "@/playback/cast/CastController.types";
import { HeroCastPill } from "@/playback/cast/HeroCastPill";
import { PlaybackSettingsSheet } from "@/playback/PlaybackSettingsSheet";
import {
  VideoSurface,
  type VideoSurfaceAudioTrack,
  type VideoSurfaceContentFit,
  type VideoSurfaceDiagnostics,
  type VideoSurfaceHandle
} from "@/playback/players/VideoSurface";
import { buildDanceVideoSource } from "@/playback/players/videoSource";
import { buildQueue, withRecitalIntro } from "@/playback/queue";
import { useSmoothPlaybackProgress } from "@/playback/useSmoothPlaybackProgress";
import { usePlaybackTelemetry } from "@/playback/usePlaybackTelemetry";
import {
  videoRenditionForDance,
  type ReportedVideoRendition
} from "@/playback/playbackTelemetry";
import { isPlaybackTimeDiscontinuity } from "@/playback/playbackProgress";
import {
  shouldPauseLocalPlaybackForAppState,
  type PictureInPicturePhase
} from "@/playback/nativePlaybackLifecycle";
import { savedDancersForRecital } from "@/playback/myDancer";
import { useWatchScreenCapture } from "@/playback/useWatchScreenCapture";
import {
  firstWatchRouteParam,
  watchBrowserUrlForCurrentDance,
  watchEntryDanceId,
  watchPathForDance
} from "@/playback/watchRoute";
import { useReadinessHold } from "@/providers/ReadinessGate";

const VALID_MODES: PlaybackMode[] = [
  "single-dance",
  "full-performance",
  "full-recital",
  "from-dance",
  "my-dancer"
];
const PLAYBACK_MAX_WIDTH = 1400;

function playbackStatsCodecLabel(
  codec: string | undefined,
  playbackEngine:
    | NonNullable<VideoSurfaceDiagnostics["active"]>["playbackEngine"]
    | undefined,
  fallback: string
) {
  return codec && codec !== "unknown"
    ? codec.toUpperCase()
    : playbackEngine ?? fallback;
}

const QUEUE_ROW_PITCH = DANCE_ROW_HEIGHT + space.md;
// Divider's overline is 14 px high with space.md above and below. A divider
// inserted between two rows also adds one extra container gap.
const QUEUE_PART_DIVIDER_PITCH = 14 + space.md * 3;
const QUEUE_FOLLOW_BUFFER_ROWS = 3;
const QUEUE_FOLLOW_CONTEXT_BUFFER = QUEUE_ROW_PITCH * QUEUE_FOLLOW_BUFFER_ROWS;
const QUEUE_SCROLL_MIN_DURATION_MS = 260;
const QUEUE_SCROLL_MAX_DURATION_MS = 500;
const QUEUE_SCROLL_FULL_DURATION_DISTANCE = QUEUE_ROW_PITCH * 3;
// Match the app's deliberate "breathe in" motion: ease gently into movement,
// then settle without the abrupt launch of a one-sided ease-out curve.
const QUEUE_SCROLL_EASING = Easing.bezier(0.4, 0, 0.2, 1);
// Short-viewport fit: reserve room below the player before sizing the video
// by height, so the stage never swallows the whole frame. Wide reserves the
// pinned now-playing bar; stacked reserves a useful slice of the scrolling
// flow (now-playing peek + first rows).
const NOW_PLAYING_RESERVE = 132;
const STACKED_QUEUE_RESERVE = 240;
/** Lets pointer/focus cross from Video.js into app-owned overlay controls. */
const WEB_CHROME_HANDOFF_MS = 160;

function startsQueueSection(
  previousDance: Dance | undefined,
  dance: Dance,
  fullRecital: boolean
): boolean {
  if (!previousDance) return false;
  return fullRecital
    ? previousDance.performanceId !== dance.performanceId ||
        previousDance.partId !== dance.partId
    : previousDance.partId !== dance.partId;
}

function initialQueueOffset(
  queue: Dance[],
  startIndex: number,
  fullRecital: boolean
): number {
  let sectionCount = 0;
  for (let index = 1; index <= startIndex; index += 1) {
    const dance = queue[index];
    if (dance && startsQueueSection(queue[index - 1], dance, fullRecital)) {
      sectionCount += 1;
    }
  }
  // This moves the selected row into exactly the position row zero naturally
  // occupies, regardless of the Now Playing/search block above the list.
  return startIndex * QUEUE_ROW_PITCH + sectionCount * QUEUE_PART_DIVIDER_PITCH;
}

// Fixture feeds for the development-only `?remote=cast` preview.
const PREVIEW_AUDIO_TRACKS: VideoSurfaceAudioTrack[] = [
  { id: "audience", label: "Audience mix" },
  { id: "clean", label: "Clean audio" }
];

/**
 * The bar's film frame wears the active list row's live gradient sliver —
 * ambient progress that survives the player chrome hiding. Own component
 * so the smoothing hook lives outside WatchScreen's early returns.
 */
function NowPlayingThumb({
  danceId,
  durationSeconds,
  fallbackSeed,
  placeholderUri,
  playing,
  progress,
  sampleIntervalMs,
  uri
}: {
  danceId: string;
  durationSeconds: number;
  fallbackSeed: string;
  placeholderUri: string | null;
  playing: boolean;
  progress: number;
  sampleIntervalMs: number;
  uri: string | null;
}) {
  const animation = useSmoothPlaybackProgress({
    currentTime: Math.min(1, Math.max(0, progress)) * durationSeconds,
    duration: durationSeconds,
    freezeWhilePaused: true,
    itemKey: `${danceId}:nowplaying`,
    mode: "glide",
    playing,
    sampleIntervalMs
  });
  const width = animation.interpolate({
    inputRange: [0, 1],
    outputRange: ["0%", "100%"]
  });
  return (
    <View style={styles.nowPlayingThumbWrap}>
      <Artwork
        uri={uri}
        placeholderUri={placeholderUri}
        fallbackSeed={fallbackSeed}
        style={styles.nowPlayingThumb}
      />
      <View style={styles.nowPlayingThumbProgressClip}>
        <View style={styles.nowPlayingThumbProgressTrack}>
          <Animated.View
            style={[styles.nowPlayingThumbProgressFillClip, { width }]}
          >
            <LinearGradient
              colors={gradients.playback as unknown as [string, string, ...string[]]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={styles.nowPlayingThumbProgressFill}
            />
          </Animated.View>
        </View>
      </View>
    </View>
  );
}

type WatchSearchParams = {
  performanceId: string;
  mode?: string;
  dance?: string;
  start?: string;
  at?: string;
  dancer?: string;
  reattach?: string;
  scope?: string;
  chrome?: string;
  fullscreen?: string;
  remote?: string;
  veil?: string;
  deviceDenied?: string;
  swapState?: string;
  displaced?: string;
};

function resolvedPlaybackMode(rawMode: string | undefined): PlaybackMode {
  return VALID_MODES.includes(rawMode as PlaybackMode)
    ? (rawMode as PlaybackMode)
    : "full-performance";
}

export default function WatchScreen() {
  const params = useLocalSearchParams<WatchSearchParams>();
  const mode = resolvedPlaybackMode(params.mode);
  const scopeKind =
    mode === "full-recital" ||
    (mode === "my-dancer" && params.scope === "recital")
      ? "recital"
      : "performance";
  const instanceKey = watchQueueInstanceKey({
    dancerId: params.dancer,
    mode,
    scopeId: params.performanceId,
    scopeKind
  });
  return <WatchScreenContent key={instanceKey} />;
}

function WatchScreenContent() {
  useWatchScreenCapture();
  const params = useLocalSearchParams<WatchSearchParams>();
  const router = useAppRouter();
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const { width, height } = useWindowDimensions();
  // Queue presentation follows the viewport, not a content-sized ancestor.
  const wide = width >= 980 && width > height;
  const mode = resolvedPlaybackMode(params.mode);
  const fullRecital =
    mode === "full-recital" ||
    (mode === "my-dancer" && params.scope === "recital");
  const scopePerformance = usePerformance(fullRecital ? undefined : params.performanceId);
  const recital = useRecital(
    fullRecital ? params.performanceId : scopePerformance?.recitalId
  );
  const recitalPerformances = usePerformances(recital?.recitalId);
  const {
    catalog,
    devMedia,
    firebaseMode,
    performanceDetailErrors,
    preparePerformance,
    status: catalogStatus
  } = useCatalog();
  const recitalArtwork = useRecitalArtwork(recital?.recitalId);
  const performanceArtwork = usePerformanceFeaturedArtwork(
    fullRecital ? undefined : params.performanceId
  );
  // Dancer playlists are a person-scoped, brand-owned surface (and may span
  // recitals someday); they wear the brand backdrop, never a performance's mood.
  const pageBackdrop = pageBackdropPresentation(
    mode === "my-dancer" ? BRAND_BACKDROP_URI : performanceArtwork ?? recitalArtwork,
    mode !== "my-dancer" && !performanceArtwork
      ? "program-cover"
      : "centered-artwork"
  );
  const pageArtwork = pageBackdrop.uri;
  const performanceDances = useDances(fullRecital ? undefined : params.performanceId);
  const performanceDetailsStatus = usePerformanceDetailsStatus(
    fullRecital ? undefined : params.performanceId
  );
  const dances = useMemo(
    () => {
      if (!fullRecital) return performanceDances;
      const performanceQueue = recitalPerformances.flatMap(
        (performance) => catalog?.dances[performance.performanceId] ?? []
      );
      return withRecitalIntro(performanceQueue, recital?.intro, mode);
    },
    [catalog?.dances, fullRecital, mode, performanceDances, recital, recitalPerformances]
  );
  const detailsStatus = fullRecital
    ? recitalPerformances.some(
        (performance) =>
          !catalog?.dances[performance.performanceId] && !performanceDetailErrors[performance.performanceId]
      )
      ? "loading"
      : recitalPerformances.some((performance) => performanceDetailErrors[performance.performanceId])
        ? "error"
        : "ready"
    : performanceDetailsStatus;
  useEffect(() => {
    if (!fullRecital) return;
    for (const performance of recitalPerformances) {
      void preparePerformance(performance.performanceId).catch(() => {});
    }
  }, [fullRecital, preparePerformance, recitalPerformances]);
  useReadinessHold(
    `watch-details-${params.performanceId ?? "unknown"}`,
    catalogStatus === "ready" &&
      (!recital || (!fullRecital && !scopePerformance) || detailsStatus !== "loading")
  );
  const devMediaUrl = useDevMediaUrl();
  const mediaUrl = useMediaUrl();
  const {
    currentDeviceId,
    devices: activatedDevices,
    recitalResume,
    savedDancers,
    resume,
    setRecitalResume,
    setResume
  } = useAccount();
  const playbackSession = usePlaybackSession();
  const playbackPresenceSampleRef = useRef<{
    danceId: string;
    positionSeconds: number;
    observedAt: number;
  } | null>(null);
  const playbackSavedDancers = useMemo(
    () => recital ? savedDancersForRecital(savedDancers, recital.recitalId) : [],
    [recital, savedDancers]
  );
  const selectedPlaybackDancers = useMemo(
    () => params.dancer
      ? playbackSavedDancers.filter((dancer) => dancer.dancerId === params.dancer)
      : playbackSavedDancers,
    [params.dancer, playbackSavedDancers]
  );
  const queueOwnerKey = useMemo(
    () =>
      playbackQueueOwnerKey({
        dancerIds: selectedPlaybackDancers.map((dancer) => dancer.dancerId),
        mode,
        scopeId: params.performanceId,
        scopeKind: fullRecital ? "recital" : "performance"
      }),
    [
      fullRecital,
      mode,
      params.performanceId,
      selectedPlaybackDancers
    ]
  );
  const {
    device: clientDevice,
    deviceLimitReached,
    ensureActiveDevice
  } = useAuthState();
  const cast = useCastController();
  const airPlay = useAirPlayController();
  const castConnected = cast.castState === "connected";
  // Development-only visual QA for the complete remote-player presentation.
  // It deliberately runs inside the native app too, so App Store screenshots
  // use the shipped header, safe-area, and status-bar presentation.
  const castPreviewActive =
    __DEV__ && params.remote === "cast";
  const { startQueue, stopQueue } = playbackSession;
  const enterStyle = useEnterTransition();
  usePageBackdrop(pageArtwork, pageBackdrop.focalY);

  // Route parameters describe how this screen boots. Live `dance=` updates
  // must never feed back into queue construction or restart the playback
  // session, so capture the entry selection and timestamp once per route.
  const entryRouteRef = useRef<{
    activationIntent: CastPlaybackQueue["activationIntent"];
    activationKey: string;
    danceId: string | undefined;
    key: string;
    position: number | undefined;
  } | null>(null);
  const entryRouteKey = queueOwnerKey;
  if (!entryRouteRef.current || entryRouteRef.current.key !== entryRouteKey) {
    const rawPosition = firstWatchRouteParam(params.at);
    const parsedPosition = rawPosition === undefined ? undefined : Number(rawPosition);
    entryRouteRef.current = {
      activationIntent: params.reattach === "1" ? "reattach" : "play",
      activationKey:
        params.reattach === "1"
          ? "reattach"
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`,
      danceId: watchEntryDanceId(params.dance, params.start),
      key: entryRouteKey,
      position:
        parsedPosition !== undefined && Number.isFinite(parsedPosition)
          ? Math.max(0, parsedPosition)
          : undefined
    };
  }
  const entryDanceId = entryRouteRef.current.danceId;
  const entryPosition = entryRouteRef.current.position;
  const routeParamsRef = useRef(params);
  routeParamsRef.current = params;
  const syncCurrentDanceRoute = useCallback(
    (danceId: string) => {
      if (Platform.OS === "web" && typeof window !== "undefined") {
        const nextUrl = watchBrowserUrlForCurrentDance(
          window.location.href,
          danceId,
          danceId === entryRouteRef.current?.danceId
        );
        const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (nextUrl !== currentUrl) {
          // This is address-bar metadata, not navigation. Going through Expo
          // Router can transiently remount the dynamic watch route, which
          // destroys Safari's AirPlay-owned video element.
          window.history.replaceState(window.history.state, "", nextUrl);
        }
        return;
      }
      const liveParams = routeParamsRef.current;
      const liveDance = firstWatchRouteParam(liveParams.dance);
      const legacyStart = firstWatchRouteParam(liveParams.start);
      const livePosition = firstWatchRouteParam(liveParams.at);
      const preserveEntryPosition =
        danceId === entryRouteRef.current?.danceId ? livePosition : undefined;
      if (
        liveDance === danceId &&
        legacyStart === undefined &&
        livePosition === preserveEntryPosition
      ) {
        return;
      }
      router.setParams({
        dance: danceId,
        start: undefined,
        at: preserveEntryPosition
      });
    },
    [router]
  );

  // Web plays through Video.js and its own control bar; native ships the
  // app's chrome. `?chrome=app` previews local app chrome in a browser, while
  // `?remote=cast` is a complete remote-player preview and enables it itself.
  // A real connected Cast session also flips web onto the app chrome: the
  // Video.js surface is unmounted, so the app's Remote is the only controls.
  const appChrome =
    Platform.OS !== "web" ||
    params.chrome === "app" ||
    castPreviewActive ||
    castConnected;
  // Development-only visual QA for the full player-frame device-limit state.
  const forceDevDeviceDenied = __DEV__ && params.deviceDenied === "1";
  const forceDevDisplaced = __DEV__ && params.displaced === "android";
  const forceDevFullscreen =
    __DEV__ && Platform.OS === "web" && params.fullscreen === "1";
  const displacedPlayback = playbackSession.displaced || forceDevDisplaced;
  const currentDeviceIsActive = activatedDevices.some(
    (device) => device.deviceId === (clientDevice?.deviceId ?? currentDeviceId)
  );
  const deviceDenied =
    forceDevDeviceDenied ||
    (deviceLimitReached && !currentDeviceIsActive) ||
    playbackSession.authorizationStatus === "device-denied";
  const swapPreviewState =
    __DEV__ && params.swapState === "pending"
      ? "pending"
      : __DEV__ && params.swapState === "quota"
        ? "quota"
        : null;
  const devicePanelOpacity = useRef(new Animated.Value(deviceDenied ? 1 : 0)).current;
  const [devicePanelMounted, setDevicePanelMounted] = useState(deviceDenied);

  useEffect(() => {
    if (deviceDenied) {
      devicePanelOpacity.stopAnimation();
      devicePanelOpacity.setValue(1);
      setDevicePanelMounted(true);
      return;
    }
    if (!devicePanelMounted) return;
    const animation = Animated.timing(devicePanelOpacity, {
      duration: 300,
      easing: Easing.out(Easing.cubic),
      toValue: 0,
      useNativeDriver: Platform.OS !== "web"
    });
    animation.start(({ finished }) => {
      if (finished) setDevicePanelMounted(false);
    });
    return () => animation.stop();
  }, [deviceDenied, devicePanelMounted, devicePanelOpacity]);

  // ?dancer=<id> narrows a my-dancer queue to one dancer (home's per-dancer
  // chips); without it the queue covers every saved dancer.
  const { queue, startIndex } = useMemo(
    () =>
      buildQueue(dances, mode, {
        startDanceId: entryDanceId,
        dancerIds: params.dancer
          ? [params.dancer]
          : selectedPlaybackDancers.map((dancer) => dancer.dancerId)
      }),
    [dances, entryDanceId, mode, params.dancer, selectedPlaybackDancers]
  );
  const initialQueueScrollY = useMemo(
    () => initialQueueOffset(queue, startIndex, fullRecital),
    [fullRecital, queue, startIndex]
  );
  const initialQueueContentOffset = useMemo(
    () => ({ x: 0, y: initialQueueScrollY }),
    [initialQueueScrollY]
  );

  const [index, setIndex] = useState(startIndex);
  const [loadGeneration, setLoadGeneration] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [localAudioTracks, setLocalAudioTracks] = useState<VideoSurfaceAudioTrack[]>([]);
  const [reportedVideoRendition, setReportedVideoRendition] =
    useState<ReportedVideoRendition>(null);
  const [playbackStatsVisible, setPlaybackStatsVisible] = useState(false);
  const [surfaceDiagnostics, setSurfaceDiagnostics] =
    useState<VideoSurfaceDiagnostics | null>(null);
  const [webAirPlayActive, setWebAirPlayActive] = useState(false);
  const [webAirPlayAvailable, setWebAirPlayAvailable] = useState(false);
  // Development-only `?remote=cast` preview: fixture feeds so the Remote's audio
  // chip renders and toggles without a live receiver.
  const [previewAudioTrack, setPreviewAudioTrack] = useState("audience");
  const [localSelectedAudioTrack, setLocalSelectedAudioTrack] = useState<string | null>(
    readPersistedAudioFeedId
  );
  const [localPreferredAudioTrack, setLocalPreferredAudioTrack] = useState<string | null>(
    readPersistedAudioFeedId
  );
  const [ended, setEnded] = useState(false);
  const [exitCoverVisible, setExitCoverVisible] = useState(false);
  // Paint a resume bookmark on the first render. The native clock takes over
  // only after the selected item has been adopted and its initial seek lands.
  const [currentTime, setCurrentTime] = useState(entryPosition ?? 0);
  const [duration, setDuration] = useState(0);
  const [chromeVisible, setChromeVisible] = useState(true);
  const [webControlsVisible, setWebControlsVisible] = useState(true);
  const [playerChromeInteractionActive, setPlayerChromeInteractionActive] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(forceDevFullscreen);
  const [fullscreenContentFit, setFullscreenContentFit] =
    useState<VideoSurfaceContentFit>("contain");
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const [queueQuery, setQueueQuery] = useState("");
  const [playerWidth, setPlayerWidth] = useState(0);
  const [queueContentHeight, setQueueContentHeight] = useState(0);
  const [queueViewportHeight, setQueueViewportHeight] = useState(0);
  // Which dance has real rendered frames — the poster veil covers everything
  // else (grant fetch, HLS startup, dance-to-dance switches).
  const [frameReadyId, setFrameReadyId] = useState<string | null>(null);
  const [presentingTimeId, setPresentingTimeId] = useState<string | null>(null);
  const [hasPresentedLocalFrame, setHasPresentedLocalFrame] = useState(false);
  const [scrollLockedForScrub, setScrollLockedForScrub] = useState(false);
  const airPlayActive = airPlay.routeActive;
  const nativePlaying = Platform.OS === "ios" ? airPlay.playing : playing;
  const webStageQueue = Platform.OS === "web" && !isFullscreen;

  useEffect(() => {
    // iOS presents the app-scoped AVPlayer through AirPlayController rather
    // than Watch's local VideoSurface callbacks. Record its first proven
    // frame in the same sticky session flag so later dance handoffs retain
    // all chrome instead of treating every veil as another cold start.
    if (Platform.OS === "ios" && airPlay.readyItemId) {
      setHasPresentedLocalFrame(true);
    }
  }, [airPlay.readyItemId]);
  // AppHeader owns fixed control and safe-area geometry. Measuring that same
  // geometry after mount made Watch compose once with the estimate and then
  // resize the entire player stage when onLayout arrived — a visible upward
  // pop on Android. Use the final value from the first frame instead.
  const headerHeight = getAppHeaderHeight(insets.top);
  const scrollTopInset = headerHeight + APP_HEADER_CONTENT_GAP;
  // Responsive chrome cannot wait for playerBox.onLayout: doing so paints
  // the regular controls/Now Playing row once, then visibly reflows both into
  // their phone layouts. The stage width is deterministic from the viewport,
  // its gutters, height fit, and (when wide) the queue rail, so seed the exact
  // same decision synchronously and let onLayout merely refine the number.
  const availablePlayerHeight = Math.max(
    200,
    height -
      scrollTopInset -
      (wide ? NOW_PLAYING_RESERVE + space.xl : STACKED_QUEUE_RESERVE)
  );
  const playerFitMaxWidth = Math.max(
    320,
    Math.round((availablePlayerHeight * 16) / 9)
  );
  const stageContentWidth = Math.max(
    0,
    Math.min(width, PLAYBACK_MAX_WIDTH) - space.xl * 2
  );
  const estimatedPlayerWidth = wide
    ? Math.max(
        0,
        stageContentWidth -
          space.xl -
          Math.min(400, stageContentWidth * 0.36)
      )
    : Math.min(stageContentWidth, playerFitMaxWidth);
  const resolvedPlayerWidth = playerWidth || estimatedPlayerWidth;

  const surfaceRef = useRef<VideoSurfaceHandle>(null);
  const terminateLocalPlayback = useCallback(() => {
    surfaceRef.current?.terminatePlayback();
  }, []);
  useTerminatePlaybackOnAuthSessionChange(terminateLocalPlayback);
  const pictureInPicturePhaseRef = useRef<PictureInPicturePhase>("idle");
  const playerBoxRef = useRef<View>(null);
  const queueScrollRef = useRef<ScrollView>(null);
  const webStageScrollRef = useRef<HTMLDivElement>(null);
  const keyboardAwareQueueRef = useRef<KeyboardAwareScrollViewRef>(null);
  const queueScrollYRef = useRef(initialQueueScrollY);
  const queueSearchFocusedRef = useRef(false);
  const queueFollowSuppressedIndexRef = useRef<number | null>(null);
  // Web keeps the visual stage fixed inside one real, full-stage ScrollView at
  // every responsive width. Only the queue flow translates from this native
  // scroll value; wheel/touch input and boundary physics stay browser-owned.
  const webStageScrollY = useRef(new Animated.Value(0)).current;
  // Row tops by queue index, measured in queue-scroll content coordinates.
  const queueRowOffsetsRef = useRef(new Map<number, number>());
  // Native starts at initialQueueContentOffset, so its first measured row must
  // not trigger a second correction during the stack reveal. Follow begins
  // after playback changes dances. Web retains its established measured jump.
  const queueFollowActivatedRef = useRef(Platform.OS === "web");
  const queueAutoScrollReadyRef = useRef(false);
  const queueScrollAnimRef = useRef<number | null>(null);
  const previousWideRef = useRef(wide);
  const previousQueueQueryRef = useRef(queueQuery);
  const getWebQueueScrollNode = useCallback(() => {
    if (Platform.OS !== "web") return null;
    return webStageScrollRef.current;
  }, []);
  // On web, both the RNW instance scrollTo and Element.scrollTo() no-op in
  // embedded Chromium; only direct scrollTop assignment moves the scroller.
  // Drive scrollTop with a short rAF ease for animated moves.
  const scrollQueueTo = useCallback((danceIndex: number, y: number, animated: boolean) => {
    const isFirstDance = danceIndex === 0;
    const isLastDance = danceIndex === queue.length - 1;
    if (Platform.OS !== "web") {
      const maximumScroll = Math.max(0, queueContentHeight - queueViewportHeight);
      const safeTop = Math.min(
        QUEUE_FOLLOW_CONTEXT_BUFFER,
        Math.max(space.md, (queueViewportHeight - DANCE_ROW_HEIGHT) / 2)
      );
      const safeBottom = Math.max(
        safeTop + DANCE_ROW_HEIGHT,
        queueViewportHeight - QUEUE_FOLLOW_CONTEXT_BUFFER
      );
      const currentY = queueScrollYRef.current;
      let target = currentY;
      if (isFirstDance) {
        target = 0;
      } else if (isLastDance) {
        target = maximumScroll;
      } else if (y < currentY + safeTop) {
        target = Math.max(0, y - safeTop);
      } else if (y + DANCE_ROW_HEIGHT > currentY + safeBottom) {
        target = y + DANCE_ROW_HEIGHT - safeBottom;
      } else {
        return;
      }
      target = Math.min(Math.max(0, target), maximumScroll);
      if (Math.abs(target - currentY) < 1) return;
      const nativeScroller = wide
        ? queueScrollRef.current
        : keyboardAwareQueueRef.current;
      nativeScroller?.scrollTo({ y: target, animated });
      return;
    }
    let node: HTMLElement | null | undefined = getWebQueueScrollNode();
    if (!node) return;
    const maximumScroll = Math.max(0, node.scrollHeight - node.clientHeight);
    const rowTop = y - node.scrollTop;
    const webViewportHeight = webStageQueue ? queueViewportHeight : node.clientHeight;
    const safeTop = Math.min(
      QUEUE_FOLLOW_CONTEXT_BUFFER,
      Math.max(space.md, (webViewportHeight - DANCE_ROW_HEIGHT) / 2)
    );
    const safeBottom = Math.max(
      safeTop + DANCE_ROW_HEIGHT,
      webViewportHeight - QUEUE_FOLLOW_CONTEXT_BUFFER
    );
    let webTarget = node.scrollTop;
    if (isFirstDance) {
      webTarget = 0;
    } else if (isLastDance) {
      webTarget = maximumScroll;
    } else if (rowTop < safeTop) {
      webTarget = Math.max(0, node.scrollTop + rowTop - safeTop);
    } else if (rowTop + DANCE_ROW_HEIGHT > safeBottom) {
      webTarget = Math.max(
        0,
        node.scrollTop + rowTop + DANCE_ROW_HEIGHT - safeBottom
      );
    } else {
      return;
    }
    if (!node) return;
    // Clamp every request to the browser's real content range. The follow band
    // is a preference, not permission to manufacture empty space after the
    // finale. Repeatedly assigning an impossible scrollTop also makes rapid
    // next/previous input flicker at the queue edges.
    const target = Math.min(Math.max(0, webTarget), maximumScroll);
    if (queueScrollAnimRef.current != null) cancelAnimationFrame(queueScrollAnimRef.current);
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!animated || prefersReducedMotion) {
      node.scrollTop = target;
      return;
    }
    const start = node.scrollTop;
    const delta = target - start;
    if (Math.abs(delta) < 1) return;
    const startedAt = performance.now();
    // Small keep-visible corrections stay responsive while larger queue jumps
    // receive enough time to read as one continuous spatial move. A new
    // command adopts the current position and cancels this frame loop, so rapid
    // transport never queues stale motion.
    const distanceProgress = Math.min(
      1,
      Math.abs(delta) / QUEUE_SCROLL_FULL_DURATION_DISTANCE
    );
    const durationMs =
      QUEUE_SCROLL_MIN_DURATION_MS +
      (QUEUE_SCROLL_MAX_DURATION_MS - QUEUE_SCROLL_MIN_DURATION_MS) *
        distanceProgress;
    const step = (now: number) => {
      const p = Math.min(1, (now - startedAt) / durationMs);
      node.scrollTop = start + delta * QUEUE_SCROLL_EASING(p);
      queueScrollAnimRef.current = p < 1 ? requestAnimationFrame(step) : null;
    };
    queueScrollAnimRef.current = requestAnimationFrame(step);
  }, [getWebQueueScrollNode, queue.length, queueContentHeight, queueViewportHeight, webStageQueue]);

  const cancelQueueScrollAnimation = useCallback(() => {
    if (Platform.OS !== "web" || queueScrollAnimRef.current == null) return;
    cancelAnimationFrame(queueScrollAnimRef.current);
    queueScrollAnimRef.current = null;
  }, []);
  useEffect(() => {
    if (Platform.OS !== "web") return;
    const playerNode = playerBoxRef.current as unknown as HTMLElement | null;
    const scroller = getWebQueueScrollNode();
    if (!playerNode || !scroller) return;

    let touchLocked = false;
    let lockedScrollTop = 0;
    let previousOverflowY = "";
    let previousTouchAction = "";

    const lockStageScroll = () => {
      if (touchLocked) return;
      touchLocked = true;
      cancelQueueScrollAnimation();
      lockedScrollTop = scroller.scrollTop;
      previousOverflowY = scroller.style.overflowY;
      previousTouchAction = scroller.style.touchAction;
      // Safari's scrolling element must be frozen directly. Descendant
      // touch-action alone does not reliably stop an already-scrollable RNW
      // ScrollView from claiming a slightly moving control tap on iPad.
      scroller.style.overflowY = "hidden";
      scroller.style.touchAction = "none";
    };
    const releaseStageScroll = () => {
      if (!touchLocked) return;
      scroller.scrollTop = lockedScrollTop;
      scroller.style.overflowY = previousOverflowY;
      scroller.style.touchAction = previousTouchAction;
      touchLocked = false;
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType === "touch") lockStageScroll();
    };

    playerNode.addEventListener("pointerdown", handlePointerDown, true);
    playerNode.addEventListener("touchstart", lockStageScroll, {
      capture: true,
      passive: true
    });
    window.addEventListener("pointerup", releaseStageScroll, true);
    window.addEventListener("pointercancel", releaseStageScroll, true);
    window.addEventListener("touchend", releaseStageScroll, true);
    window.addEventListener("touchcancel", releaseStageScroll, true);

    return () => {
      releaseStageScroll();
      playerNode.removeEventListener("pointerdown", handlePointerDown, true);
      playerNode.removeEventListener("touchstart", lockStageScroll, true);
      window.removeEventListener("pointerup", releaseStageScroll, true);
      window.removeEventListener("pointercancel", releaseStageScroll, true);
      window.removeEventListener("touchend", releaseStageScroll, true);
      window.removeEventListener("touchcancel", releaseStageScroll, true);
    };
  }, [cancelQueueScrollAnimation, getWebQueueScrollNode]);
  const handleWebStageScroll = useCallback(
    (event: ReactUIEvent<HTMLDivElement>) => {
      const y = event.currentTarget.scrollTop;
      queueScrollYRef.current = y;
      webStageScrollY.setValue(y);
    },
    [webStageScrollY]
  );
  const webStageQueueTranslateY = useMemo(
    () => Animated.multiply(webStageScrollY, -1),
    [webStageScrollY]
  );
  useEffect(
    () => () => {
      if (Platform.OS === "web" && queueScrollAnimRef.current != null) {
        cancelAnimationFrame(queueScrollAnimRef.current);
      }
    },
    []
  );
  useEffect(() => {
    const layoutChanged = previousWideRef.current !== wide;
    const filterCleared = Boolean(previousQueueQueryRef.current) && !queueQuery;
    previousWideRef.current = wide;
    previousQueueQueryRef.current = queueQuery;
    if (!layoutChanged && !filterCleared) return;

    queueRowOffsetsRef.current.clear();
    queueScrollYRef.current = 0;
    webStageScrollY.setValue(0);
    queueAutoScrollReadyRef.current = false;
    if (Platform.OS === "web" && queueScrollAnimRef.current != null) {
      cancelAnimationFrame(queueScrollAnimRef.current);
      queueScrollAnimRef.current = null;
    }
  }, [queueQuery, webStageScrollY, wide]);
  // Keep the active dance in view as the show advances — the queue is a
  // lean-back surface, so it follows the show rather than the thumb.
  useEffect(() => {
    // Searching is an explicit user task. Playback may keep advancing, but it
    // must not move the queue or steal the focused field's keyboard-safe spot.
    if (queueSearchFocusedRef.current) return;
    // Keyboard dismissal changes the scroll content height over several
    // animation frames. Do not interpret those frames as requests to return
    // to Now Playing; resume follow only after playback reaches another dance.
    if (queueFollowSuppressedIndexRef.current === index) return;
    queueFollowSuppressedIndexRef.current = null;
    // While a search filters the list, measured offsets describe the filtered
    // rows only — leave the user's result view alone.
    if (queueQuery) return;
    const y = queueRowOffsetsRef.current.get(index);
    if (y == null) return;
    if (!queueFollowActivatedRef.current) {
      queueAutoScrollReadyRef.current = true;
      return;
    }
    scrollQueueTo(index, y, queueAutoScrollReadyRef.current);
    queueAutoScrollReadyRef.current = true;
  }, [index, queueContentHeight, queueQuery, scrollQueueTo, wide]);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const webControlsHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chromeInteractionReleaseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentTimeRef = useRef(entryPosition ?? 0);
  const ambientPlayerCurrentTimeRef = useRef(entryPosition ?? 0);
  const lastSavedAt = useRef(0);
  const watchedRef = useRef<Set<string>>(new Set());
  const initialSeek = useRef(entryPosition);
  const endedRef = useRef(false);
  const currentDanceIdRef = useRef<string | null>(null);
  const persistResumeRef = useRef<(danceId: string, position: number) => void>(() => {});
  const scrubbingRef = useRef(false);
  const playingBeforeScrubRef = useRef(false);
  const transientRetryDanceRef = useRef<string | null>(null);
  const transientRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const manualAdvanceInFlightRef = useRef(false);
  const airPlayPickerOpenRef = useRef(false);
  const playingIntentRef = useRef(playing);
  const playingBeforeAirPlayPickerRef = useRef(false);
  const wasDeviceDeniedRef = useRef(deviceDenied);
  const resumeAfterDeviceSwapRef = useRef(deviceDenied && playing);
  const wasDisplacedRef = useRef(displacedPlayback);
  playingIntentRef.current = Platform.OS === "ios" ? airPlay.playing : playing;

  const handleAirPlayPickerOpened = useCallback(() => {
    airPlayPickerOpenRef.current = true;
    playingBeforeAirPlayPickerRef.current = playingIntentRef.current;
  }, []);

  const handleAirPlayPickerClosed = useCallback(() => {
    airPlayPickerOpenRef.current = false;
    const shouldResume = playingBeforeAirPlayPickerRef.current;
    playingBeforeAirPlayPickerRef.current = false;
    if (!shouldResume) return;
    // The native route picker may pause AVPlayer while it changes outputs.
    // That pause is not a user request, so resume the prior intent directly.
    airPlay.setPlaying(true);
    airPlay.resumePlayback();
  }, [airPlay]);

  useEffect(() => {
    console.log(`[play] intent=${playing} source=watch-state`);
  }, [playing]);

  // Queue may load after dances arrive; keep index in range.
  useEffect(() => setIndex(startIndex), [startIndex]);
  useEffect(() => {
    const recitalWatched = recital
      ? recitalResume[recital.recitalId]?.watchedDanceIds ?? []
      : [];
    const performanceWatched = scopePerformance
      ? resume[scopePerformance.performanceId]?.watchedDanceIds ?? []
      : [];
    watchedRef.current = new Set(
      fullRecital ? recitalWatched : [...performanceWatched, ...recitalWatched]
    );
    // Only seed once per queue scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.performanceId, fullRecital]);

  useEffect(() => {
    const wasDenied = wasDeviceDeniedRef.current;
    wasDeviceDeniedRef.current = deviceDenied;
    if (deviceDenied) {
      // Remember whether this screen was trying to play before device access
      // interrupted it. A successful swap should feel like one continuous
      // action, not leave the user at a mysteriously paused spinner.
      if (!wasDenied) resumeAfterDeviceSwapRef.current = playingIntentRef.current;
      setPlaying(false);
      return;
    }
    if (wasDenied && resumeAfterDeviceSwapRef.current) {
      resumeAfterDeviceSwapRef.current = false;
      setPlaybackError(null);
      setFrameReadyId(null);
      setPresentingTimeId(null);
      setPlaying(true);
    }
  }, [deviceDenied]);

  const current = queue[index] ?? null;
  const activeVideoRendition = videoRenditionForDance(
    reportedVideoRendition,
    current?.danceId ?? null
  );
  // The app-scoped iOS AVPlayer deliberately survives presentation changes.
  // On a local re-entry, do not let this screen's default autoplay intent
  // resume its retained old item before the newly requested dance descriptor
  // has reached the controller. The source replacement can then begin under
  // the existing first-frame veil with no outgoing audio or frame exposed.
  useEffect(() => {
    if (Platform.OS !== "ios" || airPlay.routeActive || !current) return;
    airPlay.setPlaying(
      airPlay.currentItemId === current.danceId ? playing : false
    );
  }, [
    airPlay.currentItemId,
    airPlay.routeActive,
    airPlay.setPlaying,
    current,
    playing
  ]);
  const activePerformance =
    recitalPerformances.find((performance) => performance.performanceId === current?.performanceId) ??
    scopePerformance ??
    recitalPerformances[0] ??
    null;
  useEffect(() => {
    if (current) syncCurrentDanceRoute(current.danceId);
  }, [current, syncCurrentDanceRoute]);
  // Keep this synchronous with render. AVQueuePlayer can deliver one trailing
  // time event from the outgoing item after React has adopted its successor;
  // event callbacks from that prior render must not make the new dance look
  // unready or overwrite its progress.
  currentDanceIdRef.current = current?.danceId ?? null;
  const initialDance = queue[startIndex] ?? queue[0] ?? null;
  const sourceUrl = current
    ? devMedia
      ? devMediaUrl(current.hlsPath)
      : firebaseMode
        ? playbackSession.sourceForDance(current.danceId)
        : null
    : null;
  const resolvedThumbnailUrl = current
    ? (firebaseMode ? getCachedPlaybackThumbUrl(current.danceId) : null) ??
      mediaUrl(current.thumbPath)
    : null;
  // Source identity is part of the queue's control contract. Rebuilding these
  // objects on every 250 ms progress render re-ran queue reconciliation while
  // Video.js was dispatching a user pause, allowing stale play intent to
  // immediately cancel that pause.
  const videoSource = useMemo(
    () =>
      current && sourceUrl
        ? buildDanceVideoSource({
            uri: sourceUrl,
            dance: current,
            resolvedThumbnailUrl,
            performanceArtworkUrl: performanceArtwork,
            recitalArtworkUrl: recitalArtwork!
          })
        : null,
    [current, performanceArtwork, recitalArtwork, resolvedThumbnailUrl, sourceUrl]
  );
  const previousDance = queue[index - 1] ?? null;
  const previousSourceUrl = previousDance
    ? devMedia
      ? devMediaUrl(previousDance.hlsPath)
      : firebaseMode
        ? playbackSession.sourceForDance(previousDance.danceId)
        : null
    : null;
  const previousResolvedThumbnailUrl = previousDance
    ? (firebaseMode ? getCachedPlaybackThumbUrl(previousDance.danceId) : null) ??
      mediaUrl(previousDance.thumbPath)
    : null;
  const previousVideoSource = useMemo(
    () =>
      previousDance && previousSourceUrl
        ? buildDanceVideoSource({
            uri: previousSourceUrl,
            dance: previousDance,
            resolvedThumbnailUrl: previousResolvedThumbnailUrl,
            performanceArtworkUrl: performanceArtwork,
            recitalArtworkUrl: recitalArtwork!
          })
        : null,
    [
      performanceArtwork,
      previousDance,
      previousResolvedThumbnailUrl,
      previousSourceUrl,
      recitalArtwork
    ]
  );
  const previousPreviousDance = queue[index - 2] ?? null;
  const previousPreviousSourceUrl = previousPreviousDance
    ? devMedia
      ? devMediaUrl(previousPreviousDance.hlsPath)
      : firebaseMode
        ? playbackSession.sourceForDance(previousPreviousDance.danceId)
        : null
    : null;
  const previousPreviousResolvedThumbnailUrl = previousPreviousDance
    ? (firebaseMode
        ? getCachedPlaybackThumbUrl(previousPreviousDance.danceId)
        : null) ?? mediaUrl(previousPreviousDance.thumbPath)
    : null;
  const previousPreviousVideoSource = useMemo(
    () =>
      previousPreviousDance && previousPreviousSourceUrl
        ? buildDanceVideoSource({
            uri: previousPreviousSourceUrl,
            dance: previousPreviousDance,
            resolvedThumbnailUrl: previousPreviousResolvedThumbnailUrl,
            performanceArtworkUrl: performanceArtwork,
            recitalArtworkUrl: recitalArtwork!
          })
        : null,
    [
      performanceArtwork,
      previousPreviousDance,
      previousPreviousResolvedThumbnailUrl,
      previousPreviousSourceUrl,
      recitalArtwork
    ]
  );
  const nextDance = queue[index + 1] ?? null;
  const nextSourceUrl = nextDance
    ? devMedia
      ? devMediaUrl(nextDance.hlsPath)
      : firebaseMode
        ? playbackSession.sourceForDance(nextDance.danceId)
        : null
    : null;
  const nextResolvedThumbnailUrl = nextDance
    ? (firebaseMode ? getCachedPlaybackThumbUrl(nextDance.danceId) : null) ??
      mediaUrl(nextDance.thumbPath)
    : null;
  const nextVideoSource = useMemo(
    () =>
      nextDance && nextSourceUrl
        ? buildDanceVideoSource({
            uri: nextSourceUrl,
            dance: nextDance,
            resolvedThumbnailUrl: nextResolvedThumbnailUrl,
            performanceArtworkUrl: performanceArtwork,
            recitalArtworkUrl: recitalArtwork!
          })
        : null,
    [
      nextDance,
      nextResolvedThumbnailUrl,
      nextSourceUrl,
      performanceArtwork,
      recitalArtwork
    ]
  );
  const queueItems = useMemo(
    () =>
      queue.map((dance) => ({
        danceId: dance.danceId,
        performanceId: dance.performanceId
      })),
    [queue]
  );
  const {
    recordPosition: recordTelemetryPosition,
    flush: flushTelemetry
  } = usePlaybackTelemetry({
    enabled: firebaseMode && Platform.OS !== "ios",
    sessionId: playbackSession.sessionId,
    danceId: current?.danceId ?? null,
    performanceId: current?.performanceId ?? null,
    recitalId: current?.recitalId ?? null,
    entryPositionSeconds: currentTimeRef.current,
    positionSeconds: castConnected ? cast.currentTime : currentTime,
    playing:
      !deviceDenied &&
      !displacedPlayback &&
      !ended &&
      (castConnected ? cast.playing : playing),
    playbackRate,
    transport: castConnected
      ? "cast"
      : webAirPlayActive
        ? "airplay"
        : "local",
    videoRendition:
      castConnected || webAirPlayActive ? null : activeVideoRendition
  });
  const currentTelemetryPosition = useCallback(
    () =>
      castConnected
        ? cast.currentTime
        : surfaceRef.current?.currentTime ?? currentTimeRef.current,
    [cast.currentTime, castConnected]
  );
  const flushCurrentTelemetry = useCallback(() => {
    flushTelemetry(currentTelemetryPosition());
  }, [currentTelemetryPosition, flushTelemetry]);

  const retryQueue = useCallback(async () => {
    if (!recital || !current || queueItems.length === 0) return;
    if (deviceDenied) {
      const active = await ensureActiveDevice();
      if (!active) return;
    }
    await startQueue({
      queueIdentity: queueOwnerKey,
      recitalId: recital.recitalId,
      scope: fullRecital
        ? { kind: "recital", id: recital.recitalId }
        : { kind: "performance", id: current.performanceId },
      mode,
      items: queueItems,
      nowPlaying: {
        danceId: current.danceId,
        performanceId: current.performanceId,
        title: displayTitle(current.title)
      }
    });
  }, [
    current,
    deviceDenied,
    ensureActiveDevice,
    fullRecital,
    mode,
    queueOwnerKey,
    queueItems,
    recital,
    startQueue
  ]);

  useEffect(() => {
    if (firebaseMode && recital && initialDance && queueItems.length > 0) {
      void startQueue({
        queueIdentity: queueOwnerKey,
        recitalId: recital.recitalId,
        scope: fullRecital
          ? { kind: "recital", id: recital.recitalId }
          : { kind: "performance", id: initialDance.performanceId },
        mode,
        items: queueItems,
        nowPlaying: {
          danceId: initialDance.danceId,
          performanceId: initialDance.performanceId,
          title: displayTitle(initialDance.title)
        }
      });
    }
    return () => {
      surfaceRef.current?.terminatePlayback();
      const receiverOwnsPlayback = cast.detachActivePlayback();
      const airPlayOwnsPlayback = airPlay.detachActivePlayback();
      if (firebaseMode && !receiverOwnsPlayback && !airPlayOwnsPlayback) stopQueue();
    };
  }, [
    airPlay.detachActivePlayback,
    cast.detachActivePlayback,
    firebaseMode,
    fullRecital,
    initialDance,
    mode,
    queueOwnerKey,
    queueItems,
    recital,
    startQueue,
    stopQueue
  ]);

  useEffect(() => {
    if (!firebaseMode || !current) return;
    playbackSession.updateNowPlaying({
      danceId: current.danceId,
      performanceId: current.performanceId,
      title: displayTitle(current.title)
    });
  }, [current, firebaseMode, playbackSession.updateNowPlaying]);

  useEffect(() => {
    const wasDisplaced = wasDisplacedRef.current;
    wasDisplacedRef.current = displacedPlayback;
    if (displacedPlayback) {
      setPlaying(false);
      return;
    }
    if (wasDisplaced) {
      setPlaybackError(null);
      setFrameReadyId(null);
      setPresentingTimeId(null);
      setPlaying(true);
      return;
    }
    if (playbackSession.revoked) setPlaying(false);
  }, [displacedPlayback, playbackSession.revoked]);

  const pokeChrome = useCallback(() => {
    setChromeVisible(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = nativePlaying
      ? setTimeout(() => setChromeVisible(false), 3500)
      : null;
  }, [nativePlaying]);

  const handleWebControlsVisibilityChange = useCallback((visible: boolean) => {
    if (webControlsHideTimer.current) clearTimeout(webControlsHideTimer.current);
    webControlsHideTimer.current = null;
    if (visible) {
      setWebControlsVisible(true);
      return;
    }

    // Video.js fires mouseleave before the sibling overlay receives
    // pointerenter. Keep the handoff alive for one short beat so the target
    // cannot disable its own pointer events while the cursor is entering it.
    webControlsHideTimer.current = setTimeout(() => {
      webControlsHideTimer.current = null;
      setWebControlsVisible(false);
    }, WEB_CHROME_HANDOFF_MS);
  }, []);

  const beginPlayerChromeInteraction = useCallback(() => {
    if (chromeInteractionReleaseTimer.current) {
      clearTimeout(chromeInteractionReleaseTimer.current);
      chromeInteractionReleaseTimer.current = null;
    }
    setPlayerChromeInteractionActive(true);
    pokeChrome();
  }, [pokeChrome]);

  const endPlayerChromeInteraction = useCallback(() => {
    if (chromeInteractionReleaseTimer.current) {
      clearTimeout(chromeInteractionReleaseTimer.current);
    }
    // Focus and pointer events can briefly leave one segment before entering
    // its neighbor. Delay release so crossing the chip never flickers chrome.
    chromeInteractionReleaseTimer.current = setTimeout(() => {
      chromeInteractionReleaseTimer.current = null;
      setPlayerChromeInteractionActive(false);
      pokeChrome();
    }, WEB_CHROME_HANDOFF_MS);
  }, [pokeChrome]);

  const toggleChrome = useCallback(() => {
    if (!nativePlaying) {
      pokeChrome();
      return;
    }
    if (chromeVisible) {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      hideTimer.current = null;
      setChromeVisible(false);
    } else {
      pokeChrome();
    }
  }, [chromeVisible, nativePlaying, pokeChrome]);

  useEffect(() => {
    pokeChrome();
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (webControlsHideTimer.current) clearTimeout(webControlsHideTimer.current);
      if (chromeInteractionReleaseTimer.current) {
        clearTimeout(chromeInteractionReleaseTimer.current);
      }
    };
  }, [pokeChrome]);

  const persistResume = useCallback(
    (danceId: string, position: number) => {
      if (endedRef.current || !recital || mode === "single-dance") return;
      // Never save a resume point at the tail of a dance — it would come back
      // as a useless "0:00 left" offer. Roll forward to the next dance at 0:00;
      // at the tail of the last dance, queue completion owns the state.
      const danceIndex = queue.findIndex((dance) => dance.danceId === danceId);
      const dance = queue[danceIndex];
      if (dance && dance.durationSeconds > 0 && position >= dance.durationSeconds - 1) {
        const next = queue[danceIndex + 1];
        if (!next) return;
        danceId = next.danceId;
        position = 0;
      }
      const resumeDance = queue.find((entry) => entry.danceId === danceId);
      if (!resumeDance) return;
      const updatedAt = new Date().toISOString();
      if (fullRecital) {
        setRecitalResume({
          recitalId: recital.recitalId,
          mode: mode === "my-dancer" ? "my-dancer" : "full-recital",
          performanceId: resumeDance.performanceId,
          danceId,
          positionSeconds: position,
          watchedDanceIds: [...watchedRef.current],
          updatedAt
        });
        return;
      }
      const state: ResumeState = {
        performanceId: resumeDance.performanceId,
        recitalId: recital.recitalId,
        mode: mode === "my-dancer" ? "my-dancer" : "full-performance",
        danceId,
        positionSeconds: position,
        watchedDanceIds: [...watchedRef.current],
        updatedAt
      };
      setResume(state);
    },
    [fullRecital, mode, queue, recital, setRecitalResume, setResume]
  );

  // The 5-second checkpoint cadence alone loses up to 5s of position, so
  // leaving the player flushes the exact spot (unless the show ended, where
  // the last-dance-at-0:00 state should stand).
  useEffect(() => {
    persistResumeRef.current = persistResume;
  }, [persistResume]);
  useEffect(
    () => () => {
      if (endedRef.current) return;
      const danceId = currentDanceIdRef.current;
      if (danceId) persistResumeRef.current(danceId, currentTimeRef.current);
    },
    []
  );

  const handleTime = useCallback(
    (time: number, total: number) => {
      // Tolerant live seeks intentionally make the video frame follow the
      // finger. Their timeUpdate events must not escape the player while the
      // responder is active: screen chrome, countdowns, queue progress and
      // resume persistence all stay frozen at the pre-drag position.
      if (scrubbingRef.current) return;
      if (current && currentDanceIdRef.current !== current.danceId) return;
      if (Platform.OS !== "web" && time > 0 && current) {
        setPresentingTimeId(current.danceId);
      }
      setCurrentTime(time);
      currentTimeRef.current = time;
      recordTelemetryPosition(time);
      if (total > 0) setDuration(total);
      const now = Date.now();
      if (current && now - lastSavedAt.current > 5000) {
        lastSavedAt.current = now;
        persistResume(current.danceId, time);
      }
    },
    [current, persistResume, recordTelemetryPosition]
  );

  const goTo = useCallback(
    (nextIndex: number, nativeQueuedAdvance = false) => {
      if (nextIndex < 0 || nextIndex >= queue.length) return;
      flushCurrentTelemetry();
      const dance = queue[nextIndex];
      // Advance the event-generation guard before scheduling React state.
      // AVQueuePlayer may emit the outgoing item's final timeUpdate in this
      // same native callback turn, before the next render can update the ref.
      currentDanceIdRef.current = dance?.danceId ?? null;
      initialSeek.current = undefined;
      currentTimeRef.current = 0;
      endedRef.current = false;
      queueFollowActivatedRef.current = true;
      setIndex(nextIndex);
      setLoadGeneration((value) => value + 1);
      setCurrentTime(0);
      setDuration(0);
      setFrameReadyId(nativeQueuedAdvance ? dance?.danceId ?? null : null);
      setPresentingTimeId(nativeQueuedAdvance ? dance?.danceId ?? null : null);
      setEnded(false);
      setPlaying(!castConnected);
      setPlaybackError(null);
      transientRetryDanceRef.current = null;
      if (dance) persistResume(dance.danceId, 0);
    },
    [castConnected, flushCurrentTelemetry, queue, persistResume]
  );

  const goToWithLocalTransition = useCallback(
    async (nextIndex: number) => {
      if (Platform.OS === "web" && playingIntentRef.current) {
        await surfaceRef.current?.prepareForSourceChange();
      }
      goTo(nextIndex);
    },
    [goTo]
  );

  const mirrorReceiverIndex = useCallback(
    (nextIndex: number) => {
      if (nextIndex < 0 || nextIndex >= queue.length || nextIndex === index) return;
      flushCurrentTelemetry();
      if (nextIndex > index) {
        for (let crossedIndex = index; crossedIndex < nextIndex; crossedIndex += 1) {
          const crossedDance = queue[crossedIndex];
          if (crossedDance) watchedRef.current.add(crossedDance.danceId);
        }
      }
      initialSeek.current = undefined;
      currentTimeRef.current = 0;
      endedRef.current = false;
      queueFollowActivatedRef.current = true;
      setIndex(nextIndex);
      setCurrentTime(0);
      setDuration(queue[nextIndex]?.durationSeconds ?? 0);
      setFrameReadyId(null);
      setPresentingTimeId(null);
      setEnded(false);
      // Google Cast owns transport while its receiver mirrors queue changes,
      // so the watch screen must stop driving local playback. iOS is
      // different: AirPlayController also owns the phone's local AVQueuePlayer
      // before any AirPlay route is selected. Writing `false` here after its
      // prewarmed item promotion fed a stale pause straight back into the
      // player and made manual/automatic advances intermittently stop.
      if (castConnected) setPlaying(false);
      setPlaybackError(null);
      transientRetryDanceRef.current = null;
      const dance = queue[nextIndex];
      if (dance) persistResume(dance.danceId, 0);
    },
    [castConnected, flushCurrentTelemetry, index, persistResume, queue]
  );

  const handleEnded = useCallback((nativeQueuedAdvance = false) => {
    if (current) {
      watchedRef.current.add(current.danceId);
    }
    if (index + 1 < queue.length) {
      goTo(index + 1, nativeQueuedAdvance);
    } else {
      flushCurrentTelemetry();
      endedRef.current = true;
      setEnded(true);
      setPlaying(false);
      if (recital && mode !== "single-dance" && queue[0]) {
        const completedState = {
          recitalId: recital.recitalId,
          performanceId: queue[0].performanceId,
          danceId: queue[0].danceId,
          positionSeconds: 0,
          watchedDanceIds: [...watchedRef.current],
          completed: true,
          updatedAt: new Date().toISOString()
        };
        if (fullRecital) {
          setRecitalResume({
            ...completedState,
            mode: mode === "my-dancer" ? "my-dancer" : "full-recital"
          });
        } else {
          setResume({
            ...completedState,
            mode: mode === "my-dancer" ? "my-dancer" : "full-performance"
          });
        }
      }
    }
  }, [
    current,
    fullRecital,
    flushCurrentTelemetry,
    goTo,
    index,
    mode,
    queue,
    recital,
    setRecitalResume,
    setResume
  ]);

  const goToNext = useCallback(async () => {
    if (manualAdvanceInFlightRef.current) return;
    if (castConnected) {
      cast.next();
      return;
    }
    if (Platform.OS === "ios") {
      await airPlay.next();
      return;
    }
    if (index + 1 >= queue.length) {
      handleEnded();
      return;
    }
    manualAdvanceInFlightRef.current = true;
    try {
      let nativeQueuedAdvance = false;
      if (!castConnected) {
        nativeQueuedAdvance = (await surfaceRef.current?.advanceToNext()) ?? false;
      }
      goTo(index + 1, nativeQueuedAdvance);
    } finally {
      manualAdvanceInFlightRef.current = false;
    }
  }, [airPlay.next, cast, castConnected, goTo, handleEnded, index, queue.length]);

  const goToPrevious = useCallback(async () => {
    if (manualAdvanceInFlightRef.current || index <= 0) return;
    if (castConnected) {
      cast.previous();
      return;
    }
    if (Platform.OS === "ios") {
      await airPlay.previous();
      return;
    }
    manualAdvanceInFlightRef.current = true;
    try {
      const nativeQueuedAdvance =
        (await surfaceRef.current?.advanceToPrevious()) ?? false;
      if (nativeQueuedAdvance) goTo(index - 1, true);
      else await goToWithLocalTransition(index - 1);
    } finally {
      manualAdvanceInFlightRef.current = false;
    }
  }, [airPlay.previous, cast, castConnected, goTo, goToWithLocalTransition, index]);

  const selectQueueDance = useCallback(
    async (nextIndex: number) => {
      if (nextIndex === index) return;
      if (castConnected) {
        cast.select(nextIndex);
        return;
      }
      if (nextIndex === index + 1) {
        await goToNext();
        return;
      }
      if (nextIndex === index - 1) {
        await goToPrevious();
        return;
      }
      // The native players keep the successor and bounded history warm. A
      // non-adjacent jump is not prepared and must use the guarded replacement
      // path so stale frames never masquerade as the selected dance.
      await goToWithLocalTransition(nextIndex);
    },
    [cast, castConnected, goToNext, goToPrevious, goToWithLocalTransition, index]
  );

  const handlePlaybackError = useCallback(
    (message: string) => {
      const transientNetworkFailure =
        /UnknownHostException|Unable to resolve host|network connection/i.test(message);
      if (
        transientNetworkFailure &&
        current &&
        transientRetryDanceRef.current !== current.danceId
      ) {
        const danceId = current.danceId;
        transientRetryDanceRef.current = danceId;
        console.log(`[play] transient network failure; retrying dance=${danceId}`);
        setPlaybackError(null);
        setPlaying(false);
        if (transientRetryTimerRef.current) clearTimeout(transientRetryTimerRef.current);
        transientRetryTimerRef.current = setTimeout(() => {
          transientRetryTimerRef.current = null;
          if (currentDanceIdRef.current !== danceId) return;
          setLoadGeneration((value) => value + 1);
          setPlaying(true);
        }, 700);
        return;
      }
      setPlaybackError(message);
    },
    [current]
  );

  useEffect(
    () => () => {
      if (transientRetryTimerRef.current) clearTimeout(transientRetryTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (!current || !recital) {
      cast.setActivePlayback(null);
      airPlay.setActivePlayback(null);
      return;
    }
    const playbackScopeId = fullRecital ? recital.recitalId : current.performanceId;
    const queueId = remotePlaybackQueueId(
      queueOwnerKey,
      queue.map((dance) => dance.danceId)
    );
    const remoteQueue: CastPlaybackQueue = {
      activationIntent: entryRouteRef.current?.activationIntent ?? "play",
      currentIndex: index,
      recitalId: recital.recitalId,
      recitalName: fullRecital
        ? recital.title
        : scopePerformance?.title ?? recital.title,
      getPosition: () => currentTimeRef.current,
      id: queueId,
      items: queue.map((dance) => ({
        audioTracks: dance.audioTracks,
        artworkUrl:
          mediaUrl(dance.thumbPath) ??
          (firebaseMode ? getCachedPlaybackThumbUrl(dance.danceId) : null) ??
          recitalArtwork,
        duration: dance.durationSeconds,
        id: dance.danceId,
        performanceId: dance.performanceId,
        sourceUrl: devMedia
          ? devMediaUrl(dance.hlsPath)
          : firebaseMode
            ? playbackSession.sourceForDance(dance.danceId)
            : null,
        videoSources: devMedia
          ? dance.videoSources
            ? {
                h264: devMediaUrl(dance.videoSources.h264HlsPath),
                ...(dance.videoSources.hevcHlsPath
                  ? { hevc: devMediaUrl(dance.videoSources.hevcHlsPath) }
                  : {})
              }
            : null
          : firebaseMode
            ? playbackSession.videoSourcesForDance(dance.danceId)
            : null,
        subtitle: joinWithDot([danceStyleLabel(dance), dance.className]),
        title: displayTitle(dance.title),
        urlExpiresAt: !devMedia && firebaseMode
          ? playbackSession.expiresAtForDance(dance.danceId)
          : null
      })),
      loadKey: `${queueId}:${entryRouteRef.current?.activationKey ?? "entry"}:${loadGeneration}`,
      onConnected: () => {
        setPlaybackError(null);
        setPlaying(false);
      },
      onDisconnected: (position) => {
        if (playbackSession.revoked) {
          setPlaying(false);
          return;
        }
        initialSeek.current = position;
        currentTimeRef.current = position;
        setCurrentTime(position);
        setFrameReadyId(null);
        setPlaying(true);
      },
      onItemChanged: mirrorReceiverIndex,
      onQueueEnded: handleEnded,
      receiverBadge: playbackSession.receiverBadge,
      returnPathForItem: (itemId) =>
        watchPathForDance(playbackScopeId, mode, itemId, params.dancer, {
          reattach: true,
          scope: fullRecital ? "recital" : undefined
        })
    };
    cast.setActivePlayback(remoteQueue);
    airPlay.setActivePlayback(remoteQueue);
  }, [
    airPlay.setActivePlayback,
    cast.setActivePlayback,
    current,
    devMedia,
    firebaseMode,
    handleEnded,
    index,
    loadGeneration,
    mirrorReceiverIndex,
    mode,
    queueOwnerKey,
    fullRecital,
    playbackSession.revoked,
    playbackSession.receiverBadge,
    recital,
    playbackSession.expiresAtForDance,
    playbackSession.sourceForDance,
    params.dancer,
    queue,
    recitalArtwork
  ]);

  useEffect(() => {
    if (castConnected) handleTime(cast.currentTime, cast.duration);
  }, [cast.currentTime, cast.duration, castConnected, handleTime]);

  useEffect(() => {
    if (Platform.OS === "ios") handleTime(airPlay.currentTime, airPlay.duration);
  }, [airPlay.currentTime, airPlay.duration, handleTime]);

  useEffect(() => {
    if (Platform.OS === "ios") setPlaybackError(airPlay.error);
  }, [airPlay.error]);

  const handleExitFullscreen = useCallback(() => {
    if (Platform.OS === "web" && typeof document !== "undefined") {
      if (document.fullscreenElement) {
        void document.exitFullscreen().catch((error: unknown) =>
          console.log(`[play] fullscreen exit failed: ${String(error)}`)
        );
      } else {
        setIsFullscreen(false);
      }
      return;
    }
    setIsFullscreen(false);
  }, []);

  const handleFullscreen = useCallback(() => {
    if (Platform.OS !== "web") {
      setIsFullscreen(true);
      return;
    }

    if (typeof document === "undefined") return;
    if (document.fullscreenElement) {
      handleExitFullscreen();
      return;
    }

    // Fullscreen the entire player box, not the <video> or Video.js
    // container, so the app's veil, cast pill, and custom chrome remain in
    // the same mounted presentation.
    const target = playerBoxRef.current as unknown as HTMLElement | null;
    if (!target?.requestFullscreen) return;
    // Web state is driven only by the fullscreenchange listener — an
    // optimistic set here strands the layout in a fullscreen the browser
    // refused (requestFullscreen can also throw synchronously).
    try {
      void target.requestFullscreen().catch((error: unknown) =>
        console.log(`[play] fullscreen enter failed: ${String(error)}`)
      );
    } catch (error) {
      console.log(`[play] fullscreen enter failed: ${String(error)}`);
    }
  }, [handleExitFullscreen]);

  const toggleFullscreenContentFit = useCallback(() => {
    setFullscreenContentFit((value) =>
      value === "contain" ? "cover" : "contain"
    );
    pokeChrome();
  }, [pokeChrome]);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return undefined;
    const handleFullscreenChange = () => {
      const target = playerBoxRef.current as unknown as HTMLElement | null;
      setIsFullscreen(Boolean(target && document.fullscreenElement === target));
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (Platform.OS !== "android" || !isFullscreen) return undefined;
    const subscription = BackHandler.addEventListener("hardwareBackPress", () => {
      handleExitFullscreen();
      return true;
    });
    return () => subscription.remove();
  }, [handleExitFullscreen, isFullscreen]);

  useEffect(() => {
    if (Platform.OS !== "ios") return undefined;
    const nativeNavigation = navigation as unknown as {
      addListener: (
        event: "transitionStart" | "transitionEnd" | "gestureCancel",
        listener: (event: { data?: { closing?: boolean } }) => void
      ) => () => void;
    };
    const removeTransitionStart = nativeNavigation.addListener(
      "transitionStart",
      (event) => {
        if (event.data?.closing) setExitCoverVisible(true);
      }
    );
    const removeTransitionEnd = nativeNavigation.addListener(
      "transitionEnd",
      (event) => {
        if (!event.data?.closing) setExitCoverVisible(false);
      }
    );
    const removeGestureCancel = nativeNavigation.addListener(
      "gestureCancel",
      () => setExitCoverVisible(false)
    );
    return () => {
      removeTransitionStart();
      removeTransitionEnd();
      removeGestureCancel();
    };
  }, [navigation]);

  useEffect(() => {
    void (isFullscreen ? lockPlayerLandscape() : lockAppPortrait());
  }, [isFullscreen]);

  useEffect(
    () => () => {
      void lockAppPortrait();
    },
    []
  );

  // Settings sheet state routes like every other control: cast controller
  // while casting, AirPlay controller on iOS, the local surface otherwise.
  // Casting exposes no rate control, so Speed hides there; the gear itself
  // hides when casting offers no real choice (no dead controls).
  // Catalog descriptors are loaded before navigation and are therefore the
  // first-frame capability contract. Native AVPlayer, Video.js, and Cast all
  // briefly publish an empty track list while starting or adopting another
  // item. Treating that empty list as authoritative made the Audio pill mount
  // after the rest of the chrome. Render from the descriptors immediately,
  // then reconcile with whichever live controller owns playback.
  const sourceAudioDescriptors =
    videoSource?.audioTracks ?? current?.audioTracks ?? [];
  const sourceAudioTracks: VideoSurfaceAudioTrack[] =
    sourceAudioDescriptors.map(({ id, label }) => ({ id, label }));
  const stableLocalAudioTracks =
    localAudioTracks.length > 0 ? localAudioTracks : sourceAudioTracks;
  const stableLocalSelectedAudioTrack =
    localSelectedAudioTrack &&
    stableLocalAudioTracks.some((track) => track.id === localSelectedAudioTrack)
      ? localSelectedAudioTrack
      : sourceAudioDescriptors.find((track) => track.isDefault)?.id ??
        stableLocalAudioTracks[0]?.id ??
        null;
  const handleLocalAudioTracksChange = useCallback(
    (tracks: VideoSurfaceAudioTrack[]) => {
      // Video.js clears its track list while replacing an HLS source. Keep the
      // last confirmed capabilities through that handoff so the Audio pill
      // remains mounted alongside the Cast pill. onFirstFrame reconciles the
      // list once the incoming item is genuinely ready.
      if (tracks.length > 0) setLocalAudioTracks(tracks);
    },
    []
  );
  const liveAudioTracks = castConnected
    ? cast.availableAudioTracks
    : Platform.OS === "ios"
      ? airPlay.availableAudioTracks
      : localAudioTracks;
  const settingsAudioTracks =
    liveAudioTracks.length > 0 ? liveAudioTracks : sourceAudioTracks;
  const liveSelectedTrack = castConnected
    ? cast.selectedAudioTrack
    : Platform.OS === "ios"
      ? airPlay.selectedAudioTrack
      : localPreferredAudioTrack &&
          settingsAudioTracks.some((track) => track.id === localPreferredAudioTrack)
        ? localPreferredAudioTrack
        : localSelectedAudioTrack;
  const settingsSelectedTrack =
    liveSelectedTrack &&
    settingsAudioTracks.some((track) => track.id === liveSelectedTrack)
      ? liveSelectedTrack
      : stableLocalSelectedAudioTrack;
  const settingsRate = castConnected
    ? null
    : Platform.OS === "ios"
      ? airPlay.playbackRate
      : playbackRate;
  const settingsAvailable = !castConnected || settingsAudioTracks.length > 1;

  // The Remote's first-class feed switcher. TVs hide or ignore HLS
  // alternate-audio pickers, so while casting the choice lives on the card
  // itself — the gear never appears during remote playback.
  const remoteAudioTracks = castPreviewActive
    ? PREVIEW_AUDIO_TRACKS
    : settingsAudioTracks;
  const remoteSelectedAudioTrack = castPreviewActive
    ? previewAudioTrack
    : settingsSelectedTrack;

  const handleSelectAudioTrack = useCallback(
    (id: string) => {
      // The selected feed is durable user intent, not merely a reflection of
      // whichever pipeline last emitted a change event. Safari may not echo an
      // AudioTrackList change while its video is routed through AirPlay, but
      // the active host and every promoted dance must retain this choice.
      setLocalPreferredAudioTrack(id);
      setLocalSelectedAudioTrack(id);
      if (castConnected) {
        cast.setAudioTrack(id);
      } else if (Platform.OS === "ios") {
        airPlay.setAudioTrack(id);
      } else {
        surfaceRef.current?.setAudioTrack(id);
      }
      persistAudioFeedId(id);
    },
    [airPlay, cast, castConnected]
  );

  const handleSelectPlaybackRate = useCallback(
    (rate: number) => {
      if (Platform.OS === "ios") airPlay.setPlaybackRate(rate);
      setPlaybackRate(rate);
    },
    [airPlay]
  );

  const pauseLocalPlaybackForBackground = useCallback(() => {
    flushCurrentTelemetry();
    setPlaying(false);
    if (Platform.OS === "ios") airPlay.setPlaying(false);
    else surfaceRef.current?.pausePlayback();
  }, [airPlay.setPlaying, flushCurrentTelemetry]);

  const handlePictureInPictureChange = useCallback(
    (active: boolean) => {
      // PiP stop is reported before Android returns the activity to `active`.
      // It therefore cannot itself mean "pause". Preserve play intent and let
      // the authoritative AppState transition decide ordinary backgrounding.
      pictureInPicturePhaseRef.current = active ? "active" : "idle";
    },
    []
  );

  const handlePictureInPicture = useCallback(() => {
    // Suppress iOS's intermediate `inactive` lifecycle event before asking
    // AVKit to establish PiP. The native start/stop callbacks own the phase
    // after this request; PiP itself never changes the user's play intent.
    pictureInPicturePhaseRef.current = "requesting";
    try {
      const request = surfaceRef.current?.requestPictureInPicture();
      if (request) {
        void request.catch((error: unknown) => {
          pictureInPicturePhaseRef.current = "idle";
          console.log(`[play] picture-in-picture failed: ${String(error)}`);
          if (
            shouldPauseLocalPlaybackForAppState({
              appState: AppState.currentState,
              pictureInPicturePhase: "idle",
              platform: Platform.OS,
              remotePlaybackActive: castConnected || airPlayActive
            })
          ) {
            pauseLocalPlaybackForBackground();
          }
        });
      } else {
        pictureInPicturePhaseRef.current = "idle";
      }
    } catch (error) {
      pictureInPicturePhaseRef.current = "idle";
      console.log(`[play] picture-in-picture failed: ${String(error)}`);
    }
  }, [airPlayActive, castConnected, pauseLocalPlaybackForBackground]);

  useEffect(() => {
    if (Platform.OS === "web") return undefined;
    const subscription = AppState.addEventListener("change", (nextState) => {
      if (
        !shouldPauseLocalPlaybackForAppState({
          appState: nextState,
          pictureInPicturePhase: pictureInPicturePhaseRef.current,
          platform: Platform.OS,
          remotePlaybackActive: castConnected || airPlayActive
        })
      ) {
        return;
      }
      pauseLocalPlaybackForBackground();
    });
    return () => subscription.remove();
  }, [airPlayActive, castConnected, pauseLocalPlaybackForBackground]);

  useEffect(() => {
    if (
      !shouldPauseLocalPlaybackForAppState({
        appState: AppState.currentState,
        pictureInPicturePhase: pictureInPicturePhaseRef.current,
        platform: Platform.OS,
        remotePlaybackActive: castConnected || airPlayActive
      })
    ) {
      return;
    }
    pauseLocalPlaybackForBackground();
  }, [airPlayActive, castConnected, pauseLocalPlaybackForBackground]);

  useEffect(() => {
    if (Platform.OS !== "web" || !playbackStatsVisible) {
      setSurfaceDiagnostics(null);
      return undefined;
    }
    const update = () => {
      setSurfaceDiagnostics(surfaceRef.current?.diagnostics ?? null);
    };
    update();
    const timer = window.setInterval(update, 500);
    return () => window.clearInterval(timer);
  }, [playbackStatsVisible]);

  // Web keyboard control: space/k play-pause, arrows seek, m mute, f fullscreen.
  // Match Netflix's deliberately obscure diagnostics chord so ordinary typing
  // can never reveal the playback-stats panel by accident.
  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.ctrlKey &&
        event.altKey &&
        event.shiftKey &&
        event.code === "KeyD"
      ) {
        event.preventDefault();
        setPlaybackStatsVisible((value) => !value);
        return;
      }
      const target = event.target as HTMLElement | null;
      // BUTTON covers Video.js controls — they handle space/enter themselves.
      if (target && ["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(target.tagName))
        return;
      // The Video.js skin owns its focused hotkeys so one keypress cannot
      // toggle both the player and this route-level fallback.
      if (target?.closest(".media-minimal-skin")) return;
      switch (event.key) {
        case " ":
        case "k":
          event.preventDefault();
          flushCurrentTelemetry();
          setPlaying((value) => !value);
          break;
        case "ArrowLeft":
          event.preventDefault();
          flushCurrentTelemetry();
          surfaceRef.current?.seek(Math.max(0, currentTimeRef.current - 10));
          break;
        case "ArrowRight":
          event.preventDefault();
          flushCurrentTelemetry();
          surfaceRef.current?.seek(currentTimeRef.current + 10);
          break;
        case "m":
          setMuted((value) => !value);
          break;
        case "f":
          handleFullscreen();
          break;
      }
      pokeChrome();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [flushCurrentTelemetry, handleFullscreen, pokeChrome]);

  // Dashboard presence follows confirmed forward media-clock movement, not
  // whether the watch route (or a paused player) happens to remain mounted.
  useEffect(() => {
    const presencePlaying = castPreviewActive
      ? false
      : castConnected
        ? cast.playing
        : Platform.OS === "ios"
          ? airPlay.playing
          : playing;
    if (!current || deviceDenied || displacedPlayback || ended || !presencePlaying) {
      playbackPresenceSampleRef.current = null;
      playbackSession.reportPlaybackActivity(false);
      return;
    }

    const positionSeconds = castConnected
      ? cast.currentTime
      : Platform.OS === "ios"
        ? airPlay.currentTime
        : currentTime;
    if (!Number.isFinite(positionSeconds)) return;
    const observedAt = Date.now();
    const previous = playbackPresenceSampleRef.current;
    if (
      previous?.danceId === current.danceId &&
      positionSeconds > previous.positionSeconds + 0.02 &&
      !isPlaybackTimeDiscontinuity(
        previous.positionSeconds,
        positionSeconds,
        observedAt - previous.observedAt,
        playbackRate
      )
    ) {
      playbackSession.reportPlaybackActivity(true);
    }
    playbackPresenceSampleRef.current = {
      danceId: current.danceId,
      positionSeconds,
      observedAt
    };
  }, [
    airPlay.currentTime,
    airPlay.playing,
    cast.currentTime,
    cast.playing,
    castConnected,
    castPreviewActive,
    current,
    currentTime,
    deviceDenied,
    displacedPlayback,
    ended,
    playbackRate,
    playbackSession,
    playing
  ]);

  if (playbackSession.unavailableMessage) {
    return (
      <View style={[styles.screen, { paddingTop: insets.top }]}>
        <View style={styles.emptyWrap}>
          <Text variant="title">This recital isn’t available</Text>
          <Muted>{playbackSession.unavailableMessage}</Muted>
          <Button label="Back" variant="secondary" onPress={() => router.back()} />
        </View>
      </View>
    );
  }

  // Still composing (deep link / refresh): the curtain is covering — never
  // flash a "missing" message while the catalog is on its way.
  if (!recital || (!fullRecital && !scopePerformance)) {
    if (catalogStatus !== "ready") {
      return <View style={styles.screen} />;
    }
    return (
      <View style={[styles.screen, { paddingTop: insets.top }]}>
        <View style={styles.emptyWrap}>
          <Text variant="title">This recital isn’t in your library.</Text>
          <Muted>Everything you can watch lives in your recitals.</Muted>
          <Button
            label="Go to my recitals"
            variant="secondary"
            onPress={() => router.replace("/recitals")}
          />
        </View>
      </View>
    );
  }


  // In-app watch navigation prepares the queue before pushing. A direct cold
  // link stays behind the root curtain until dances and thumbnails compose.
  if (detailsStatus === "loading") return <View style={styles.screen} />;

  if (detailsStatus === "error") {
    return (
      <View style={[styles.screen, { paddingTop: insets.top }]}>
        <View style={styles.emptyWrap}>
          <Text variant="title">We couldn’t load this recital.</Text>
          <Muted>Check your connection and try again.</Muted>
          <Button label="Back" variant="secondary" onPress={() => router.back()} />
        </View>
      </View>
    );
  }

  if (queue.length === 0) {
    return (
      <View style={[styles.screen, { paddingTop: insets.top }]}>
        <View style={styles.emptyWrap}>
          <Text variant="title">Nothing to play yet.</Text>
          <Button label="Back" variant="secondary" onPress={() => router.back()} />
        </View>
      </View>
    );
  }

  // Media is coming (signed grant or dev server): the veil holds the frame.
  // Only the no-media sample catalog shows the rehearsal notice instead.
  const mediaExpected = devMedia || firebaseMode;
  // Development-only visual QA: append `?veil=1` (or `&veil=1`) to pin the
  // real shared loading surface over either player implementation.
  const forceDevVeil = __DEV__ && params.veil === "1";
  const remotePlaybackActive = castConnected || castPreviewActive || airPlayActive;
  const effectiveFrameReadyId =
    Platform.OS === "ios" ? airPlay.readyItemId : frameReadyId;
  const effectivePresentingTimeId =
    Platform.OS === "ios" ? airPlay.readyItemId : presentingTimeId;
  const veiled =
    forceDevVeil ||
    (!displacedPlayback &&
      !remotePlaybackActive &&
      mediaExpected &&
      !deviceDenied &&
      playbackSession.authorizationStatus !== "error" &&
      !playbackError &&
      !ended &&
      (!current ||
        effectiveFrameReadyId !== current.danceId ||
        (Platform.OS !== "web" && effectivePresentingTimeId !== current.danceId)));
  // Queue rail only fits landscape-shaped viewports; portrait tablets stack.
  const upNext = queue[index + 1] ?? null;
  const previewDuration = current?.durationSeconds || 100;
  const playerPlaying = castPreviewActive
    ? true
    : castConnected
      ? cast.playing
      : Platform.OS === "ios"
        ? airPlay.playing
        : playing;
  const webPlayerOwnsControls = Platform.OS === "web" && !appChrome;
  const castPillLocalVisible = webPlayerOwnsControls
    ? webControlsVisible || playerChromeInteractionActive
    : chromeVisible || !playerPlaying;
  // The next source intentionally raises the loading veil, but that is one
  // continuous playback session after the first real frame has appeared. Keep
  // all top chrome stable through that handoff; initial load, terminal end,
  // errors, and the forced veil QA state still use the normal hidden state.
  const retainingTopChromeThroughDanceHandoff =
    hasPresentedLocalFrame &&
    veiled &&
    !forceDevVeil &&
    !deviceDenied &&
    !playbackError &&
    !ended &&
    Boolean(current);
  const playerTopChromeVisible =
    remotePlaybackActive ||
    ((!veiled || retainingTopChromeThroughDanceHandoff) && castPillLocalVisible);
  const playerCurrentTime = castPreviewActive
    ? Math.min(40, previewDuration * 0.4)
    : castConnected
      ? cast.currentTime
      : Platform.OS === "ios"
        ? airPlay.currentItemId === current?.danceId
          ? airPlay.currentTime
          : currentTime
        : currentTime;
  const playerDuration = castPreviewActive
    ? previewDuration
    : castConnected
      ? cast.duration
      : Platform.OS === "ios"
        ? airPlay.duration
        : duration;
  // The scrubber owns transient seek preview. Everything outside the player
  // chrome (Now Playing, queue rows, and the up-next countdown) must continue
  // to describe confirmed playback until the drag ends. This extra boundary
  // matters for iOS and Cast because their controller clocks bypass the local
  // VideoSurface onTime guard above.
  if (!scrubbingRef.current) {
    ambientPlayerCurrentTimeRef.current = playerCurrentTime;
  }
  const ambientPlayerCurrentTime = ambientPlayerCurrentTimeRef.current;
  const activeDurationSeconds = playerDuration || current?.durationSeconds || 0;
  const activeProgress =
    activeDurationSeconds > 0
      ? Math.min(1, ambientPlayerCurrentTime / activeDurationSeconds)
      : 0;
  const stackNowPlaying = resolvedPlayerWidth < 480;
  const devicePanelMinHeight = wide ? resolvedPlayerWidth * (9 / 16) : undefined;
  const framelessDevicePanel = devicePanelMounted && !wide;
  const displacedSession = forceDevDisplaced
    ? {
        deviceId: "dev-android",
        deviceLabel: spokenDeviceName({
          platform: "android",
          formFactor: "phone",
          operatingSystem: "Android"
        }),
        deviceFormFactor: "phone" as const,
        nowPlaying: {
          danceId: dances[0]!.danceId,
          performanceId: dances[0]!.performanceId,
          title: displayTitle(dances[0]!.title)
        }
      }
    : playbackSession.displacedSession;
  const displacedPresentation = selectDisplacedPlayback({
    session: displacedSession,
    fallbackDance: current,
    dances,
    recitalArtwork,
    mediaUrl
  });
  // A whisper of a countdown in the final 30 seconds of a dance.
  const remainingSeconds = Math.max(
    0,
    (playerDuration || current?.durationSeconds || 0) - ambientPlayerCurrentTime
  );
  const upNextCountdown =
    upNext && remainingSeconds > 0 && remainingSeconds <= 30
      ? `in ${formatDuration(remainingSeconds)}`
      : null;
  const remotePlaybackDestination = castPreviewActive
    ? "Living Room TV"
    : castConnected
      ? cast.deviceName ?? "your TV"
      : airPlayActive
        ? "AirPlay"
        : null;
  // The Remote card carries no dance title — the Now Playing card directly
  // beneath owns identity; the chrome renders the "Playing on …" eyebrow
  // over its center-stage transport.
  const remotePlaybackContents = remotePlaybackDestination ? (
    <Backdrop
      uri={current ? mediaUrl(current.thumbPath) ?? recitalArtwork : recitalArtwork}
      fallbackSeed={current?.danceId ?? activePerformance?.performanceId ?? recital.recitalId}
      intensity={1}
    />
  ) : null;
  const modeLabel =
    mode === "my-dancer"
      ? selectedPlaybackDancers.length === 1
        ? `${selectedPlaybackDancers[0].name.split(" ")[0]}’s dances`
        : "My dancers’ dances"
      : fullRecital
        ? recital.title
        : activePerformance?.title ?? recital.title;

  const savedDancerIds = new Set(
    selectedPlaybackDancers.map((dancer) => dancer.dancerId)
  );
  const visibleQueue = queueQuery ? searchDances(queue, queueQuery) : queue;
  const partTitleById = new Map(
    recitalPerformances.flatMap((performance) =>
      performance.parts.map((part) => [
        `${performance.performanceId}:${part.partId}`,
        fullRecital
          ? performance.parts.length > 1
            ? joinWithDot([performance.title, part.title])
            : performance.title
          : part.title
      ] as const)
    )
  );
  const queueHeading =
    mode === "my-dancer"
      ? selectedPlaybackDancers.length === 1
        ? `${selectedPlaybackDancers[0].name.split(" ")[0]}’s dances`
        : "My dancers’ dances"
      : "All dances";

  // Now playing card — the dance's own thumb glows behind it. On narrow
  // columns the up-next block drops below the text instead of crushing it
  // into ellipses.
  const nowPlayingBar = !isFullscreen ? (
    <View
      style={[
        styles.nowPlaying,
        wide ? styles.nowPlayingWide : styles.nowPlayingCollapsed,
        stackNowPlaying && styles.nowPlayingStacked
      ]}
    >
      <Backdrop
        uri={current ? mediaUrl(current.thumbPath) : null}
        fallbackSeed={current?.danceId ?? "now"}
        intensity={0.9}
      />
      <View style={styles.nowPlayingMain}>
        {/* The dance's own face anchors the bar — the list rows'
            film-frame, sans number badge: the eyebrow counts. */}
        {current ? (
          <NowPlayingThumb
            danceId={current.danceId}
            durationSeconds={activeDurationSeconds || current.durationSeconds}
            fallbackSeed={current.danceId}
            placeholderUri={current.blurThumb}
            playing={playerPlaying && !deviceDenied}
            progress={activeProgress}
            sampleIntervalMs={castConnected ? 1000 : 500}
            uri={mediaUrl(current.thumbPath)}
          />
        ) : null}
        <View style={styles.nowPlayingText}>
          <Overline numberOfLines={1}>
            {/* While device-blocked nothing is playing — the card is
                the promise of what the swap unlocks. */}
            {deviceDenied ? "Ready to play" : "Now playing"}
            {queue.length > 1 ? `${DOT_SEPARATOR}${index + 1} of ${queue.length}` : ""}
          </Overline>
          <Text variant="title" numberOfLines={1} style={styles.nowTitle}>
            {current ? displayTitle(current.title) : ""}
          </Text>
          <Muted variant="small" numberOfLines={1}>
            {joinWithDot([
              current ? danceStyleLabel(current) : null,
              current?.className
            ])}
          </Muted>
        </View>
      </View>
      {upNext ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Up next: ${displayTitle(upNext.title)}`}
          onPress={() => void goToNext()}
          style={({ hovered }) => [
            stackNowPlaying ? styles.upNextRow : styles.upNext,
            hovered && (stackNowPlaying ? styles.upNextRowHovered : styles.upNextHovered)
          ]}
        >
          {!stackNowPlaying ? <SpecularEdge cornerRadius={radius.md} /> : null}
          {stackNowPlaying ? (
            <>
              <Text
                variant="small"
                color={palette.textSoft}
                numberOfLines={1}
                style={styles.upNextRowTitle}
              >
                {displayTitle(upNext.title)}
              </Text>
              <Muted variant="small" accessibilityElementsHidden>
                ·
              </Muted>
              <Muted
                variant="small"
                numberOfLines={1}
                style={styles.upNextRowCountdown}
              >
                Up next{upNextCountdown ? ` ${upNextCountdown}` : ""}
              </Muted>
            </>
          ) : (
            <>
              <Muted variant="smallStrong" numberOfLines={1}>
                Up next{upNextCountdown ? ` ${upNextCountdown}` : ""}
              </Muted>
              <Text variant="small" color={palette.textSoft} numberOfLines={1}>
                {displayTitle(upNext.title)}
              </Text>
            </>
          )}
        </Pressable>
      ) : null}
    </View>
  ) : null;

  const queueRows =
    visibleQueue.length === 0 ? (
      <Muted variant="small" style={styles.queueEmpty}>
        No dances match “{queueQuery}”.
      </Muted>
    ) : (
      visibleQueue.map((dance, visibleIndex) => {
        const danceIndex = queue.indexOf(dance);
        const previousDance = visibleQueue[visibleIndex - 1];
        const startsNewPart =
          !queueQuery && startsQueueSection(previousDance, dance, fullRecital);
        return (
          <Fragment key={dance.danceId}>
            {startsNewPart ? (
              <Divider
                label={partTitleById.get(`${dance.performanceId}:${dance.partId}`)}
              />
            ) : null}
            <View
              onLayout={(event) => {
                const rowY = event.nativeEvent.layout.y;
                queueRowOffsetsRef.current.set(danceIndex, rowY);
                if (
                  danceIndex === index &&
                  !queueSearchFocusedRef.current &&
                  queueFollowSuppressedIndexRef.current !== danceIndex &&
                  !queueAutoScrollReadyRef.current &&
                  queueContentHeight > 0 &&
                  queueViewportHeight > 0
                ) {
                  if (!queueFollowActivatedRef.current) {
                    queueAutoScrollReadyRef.current = true;
                    return;
                  }
                  // In stacked full-performance playback, dance one already sits just
                  // below the bar and header. Preserve scrollTop=0 so a cold
                  // load does not immediately hide its first two children.
                  scrollQueueTo(
                    danceIndex,
                    !wide && danceIndex === 0 ? 0 : rowY,
                    false
                  );
                  queueAutoScrollReadyRef.current = true;
                }
              }}
            >
              <DanceRow
                dance={dance}
                active={danceIndex === index}
                activeSurface
                playbackProgress={danceIndex === index ? activeProgress : undefined}
                playbackPlaying={danceIndex === index && playerPlaying}
                playbackSampleIntervalMs={castConnected ? 1000 : 500}
                watched={watchedRef.current.has(dance.danceId)}
                onPress={() => void selectQueueDance(danceIndex)}
                highlightDancerIds={savedDancerIds}
              />
            </View>
          </Fragment>
        );
      })
    );

  // The stage owns vertical boundary behavior, but horizontal overscroll must
  // remain browser-owned so Mac trackpad Back/Forward gestures still work.
  // Using the shorthand here would silently set the X axis to `contain` too.
  const queueScrollOverscroll =
    Platform.OS === "web" ? ({ overscrollBehaviorY: "contain" } as ViewStyle) : null;
  // Only the ordinary safe-area inset belongs after the finale. The
  // keep-visible algorithm still prefers three following rows, but naturally
  // gives up that context as the real end of the list approaches.
  const queueBottomInset =
    insets.bottom + (Platform.OS === "web" ? space.xl : space.md);
  const webStageScrollDistance = webStageQueue
    ? Math.max(0, queueContentHeight - queueViewportHeight)
    : 0;
  const handleBackToRecital = () => {
    if (Platform.OS === "ios") setExitCoverVisible(true);
    if (router.canGoBack()) {
      router.back();
      return;
    }
    hintWebNavigationDirection(-1);
    router.replace(
      (fullRecital
        ? recitalPath(recital)
        : scopePerformance
          ? performancePath(recital, scopePerformance.performanceId)
          : recitalPath(recital)) as never
    );
  };
  const handleQueueSearchFocus = () => {
    queueSearchFocusedRef.current = true;
  };
  const handleQueueSearchBlur = () => {
    queueSearchFocusedRef.current = false;
    queueFollowSuppressedIndexRef.current = index;
  };
  const stackedQueueContents = (
    <>
      {nowPlayingBar}
      <View style={styles.stackedListHeader}>
        <DanceListHeader
          title={queueHeading}
          value={queueQuery}
          onChangeText={setQueueQuery}
          onSearchBlur={handleQueueSearchBlur}
          onSearchFocus={handleQueueSearchFocus}
        />
      </View>
      {queueRows}
    </>
  );
  // Wide keeps the search outside the masked queue viewport. The mask owns
  // the scrolling rows on both web and native, so content truly alpha-fades
  // before the search instead of being painted over by a sticky sibling.
  const queueList = wide ? (
    <View style={[styles.queuePanel, styles.queuePanelWide]}>
      <DanceListHeader
        title={queueHeading}
        value={queueQuery}
        onChangeText={setQueueQuery}
        onSearchBlur={handleQueueSearchBlur}
        onSearchFocus={handleQueueSearchFocus}
      />
      <ScrollEdgeFade
        top={space.xl}
        bottom={space.xl}
        style={styles.queueFadeBleed}
        onLayout={
          webStageQueue
            ? (event) => setQueueViewportHeight(event.nativeEvent.layout.height)
            : undefined
        }
      >
        {webStageQueue ? (
          <Animated.View
            onLayout={(event) => setQueueContentHeight(event.nativeEvent.layout.height)}
            style={[
              styles.queueScrollBody,
              { paddingBottom: queueBottomInset },
              {
                transform: [
                  { translateY: webStageQueueTranslateY }
                ]
              }
            ]}
          >
            {queueRows}
          </Animated.View>
        ) : (
          <ScrollView
            ref={queueScrollRef}
            contentOffset={initialQueueContentOffset}
            onContentSizeChange={(_width, contentHeight) =>
              setQueueContentHeight(contentHeight)
            }
            onLayout={(event) => setQueueViewportHeight(event.nativeEvent.layout.height)}
            onScroll={(event) => {
              queueScrollYRef.current = event.nativeEvent.contentOffset.y;
            }}
            scrollEventThrottle={16}
            scrollEnabled={!scrollLockedForScrub}
            style={[styles.queueScroll, queueScrollOverscroll]}
            contentContainerStyle={[
              styles.queueScrollBody,
              { paddingBottom: queueBottomInset }
            ]}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
          >
            {queueRows}
          </ScrollView>
        )}
      </ScrollEdgeFade>
    </View>
  ) : (
    <ScrollEdgeFade
      top={space.xl}
      bottom={space.xl}
      style={styles.stackedFlow}
      onLayout={
        webStageQueue
          ? (event) => setQueueViewportHeight(event.nativeEvent.layout.height)
          : undefined
      }
    >
      {webStageQueue ? (
        <Animated.View
          onLayout={(event) => setQueueContentHeight(event.nativeEvent.layout.height)}
          style={[
            styles.stackedFlowBody,
            { paddingBottom: queueBottomInset },
            { transform: [{ translateY: webStageQueueTranslateY }] }
          ]}
        >
          {stackedQueueContents}
        </Animated.View>
      ) : (
        <KeyboardAwareScrollView
          ref={keyboardAwareQueueRef}
          bottomOffset={SEARCH_KEYBOARD_GAP}
          contentOffset={initialQueueContentOffset}
          disableScrollOnKeyboardHide
          onContentSizeChange={(_width, contentHeight) =>
            setQueueContentHeight(contentHeight)
          }
          onLayout={(event) => setQueueViewportHeight(event.nativeEvent.layout.height)}
          onScroll={(event) => {
            queueScrollYRef.current = event.nativeEvent.contentOffset.y;
          }}
          scrollEventThrottle={16}
          scrollEnabled={!scrollLockedForScrub}
          style={[styles.queueScroll, queueScrollOverscroll]}
          contentContainerStyle={[
            styles.stackedFlowBody,
            { paddingBottom: queueBottomInset }
          ]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
        >
          {stackedQueueContents}
        </KeyboardAwareScrollView>
      )}
    </ScrollEdgeFade>
  );

  // On web this must be a passive DOM boundary. A React Native Web Pressable
  // here competes with the stage ScrollView before Video.js controls receive
  // touch input on iPad. Native keeps its existing Pressable surface.
  const PlayerInteractionSurface = Platform.OS === "web" ? View : Pressable;

  const watchViewport = (
    <>
      <View
        style={[
          styles.headerWrap,
          isFullscreen && styles.fullscreenHidden,
          { pointerEvents: isFullscreen ? "none" : "auto" }
        ]}
      >
        {Platform.OS === "android" ? (
          <HeaderBackdropScrim style={styles.headerBackdrop} />
        ) : null}
        <Animated.View style={[styles.headerContent, enterStyle]}>
          <AppHeader
            back
            backLabel={modeLabel}
            backHref={
              fullRecital
                ? recitalPath(recital)
                : scopePerformance
                  ? performancePath(recital, scopePerformance.performanceId)
                  : recitalPath(recital)
            }
            onBackPress={handleBackToRecital}
          />
        </Animated.View>
      </View>

      <HeaderContentMask
        disabled={isFullscreen}
        height={headerHeight}
      >
        <View
          style={[
            styles.stageFrame,
            { paddingTop: scrollTopInset },
            isFullscreen && styles.stageFrameFullscreen
          ]}
        >
          <Animated.View
            style={[
              styles.stage,
              wide && styles.stageWide,
              enterStyle,
              isFullscreen && styles.stageFullscreen
            ]}
          >
          <View
            style={[
              styles.playerColumn,
              Platform.OS === "web" && styles.webPlayerForeground,
              wide && styles.playerColumnWide,
              !isFullscreen && { maxWidth: playerFitMaxWidth },
              !isFullscreen && !wide && styles.playerColumnCentered,
              isFullscreen && styles.playerColumnFullscreen
            ]}
          >
            <View
              ref={playerBoxRef}
              style={[
                styles.playerBox,
                webTouchPlayerSurface,
                framelessDevicePanel && styles.playerBoxFrameless,
                devicePanelMounted
                  ? [styles.playerBoxDeviceSwap, { minHeight: devicePanelMinHeight }]
                  : !isFullscreen && styles.playerBoxVideo,
                isFullscreen && styles.playerBoxFullscreen
              ]}
              onLayout={(event) => setPlayerWidth(event.nativeEvent.layout.width)}
            >
              {!deviceDenied ? (
                <PlayerInteractionSurface
                  style={[
                    styles.playerInteractive,
                    devicePanelMounted && styles.playerInteractiveBehindSwap
                  ]}
                >
              {displacedPlayback ? (
                <DisplacedPlaybackPanel
                  artworkUri={displacedPresentation.artworkUri}
                  compact={resolvedPlayerWidth < 560}
                  eyebrow={displacedPresentation.eyebrow}
                  fallbackSeed={displacedPresentation.fallbackSeed}
                  loading={playbackSession.takingOver}
                  title={displacedPresentation.title}
                  onWatchHere={() => {
                    if (forceDevDisplaced) {
                      router.setParams({ displaced: "" });
                      return;
                    }
                    void playbackSession.takeOverCurrentPlayback();
                  }}
                />
              ) : castConnected || castPreviewActive ? (
                <View style={styles.castingPanel}>
                  {!ended ? remotePlaybackContents : null}
                </View>
              ) : mediaExpected ? (
                <>
                  {Platform.OS === "ios" ? (
                    <AirPlayVideoPresentation
                      ref={surfaceRef}
                      concealed={veiled || airPlayActive}
                      contentFit={isFullscreen ? fullscreenContentFit : "contain"}
                      onPictureInPictureChange={handlePictureInPictureChange}
                    />
                  ) : (
                    <VideoSurface
                      ref={surfaceRef}
                      src={videoSource}
                      previousSrc={previousVideoSource}
                      previousPreviousSrc={previousPreviousVideoSource}
                      nextSrc={nextVideoSource}
                      sourceGeneration={`${current?.danceId ?? "none"}:${loadGeneration}`}
                      // Web queue elements persist before the async grant URL
                      // exists. Preserve autoplay intent at element creation;
                      // VideoSurface lets source arrival drive native autoplay.
                      // Native keeps its established source-ready contract.
                      playing={
                        Platform.OS === "web"
                          ? playing
                          : playing && Boolean(sourceUrl)
                      }
                      muted={muted}
                      preferredAudioTrack={localPreferredAudioTrack}
                      playbackRate={playbackRate}
                      initialPosition={initialSeek.current}
                      contentFit={isFullscreen ? fullscreenContentFit : "contain"}
                      concealed={Platform.OS !== "web" && veiled}
                      showControls={!appChrome && !deviceDenied}
                      eyebrow={
                        joinWithDot([
                          current ? danceStyleLabel(current) : null,
                          current?.className
                        ]) ||
                        undefined
                      }
                      title={current ? displayTitle(current.title) : undefined}
                      titlePinned={veiled && !deviceDenied}
                      controlsPinned={playerChromeInteractionActive}
                      thumbnailsVtt={
                        current
                          ? devMedia
                            ? devMediaUrl(current.storyboardVttPath)
                            : playbackSession.storyboardForDance(current.danceId) ?? undefined
                          : undefined
                      }
                      onTime={handleTime}
                      onSeekStart={flushTelemetry}
                      onEnded={handleEnded}
                      onError={handlePlaybackError}
                      onPlayingChange={(nextPlaying) => {
                        if (
                          sourceUrl &&
                          !scrubbingRef.current &&
                          !airPlayPickerOpenRef.current
                        ) {
                          setPlaying(nextPlaying);
                        }
                      }}
                      onMutedChange={setMuted}
                      onPlaybackRateChange={setPlaybackRate}
                      onExternalPlaybackChange={(active) => {
                        if (Platform.OS === "web") {
                          setWebAirPlayActive(active);
                        }
                      }}
                      onRemotePlaybackAvailabilityChange={(availability) => {
                        if (Platform.OS === "web") {
                          setWebAirPlayAvailable(
                            availability === "available"
                          );
                        }
                      }}
                      onPictureInPictureChange={handlePictureInPictureChange}
                      onControlsVisibilityChange={handleWebControlsVisibilityChange}
                      onFirstFrame={() => {
                        const danceId = current?.danceId ?? null;
                        const settledTracks = surfaceRef.current?.availableAudioTracks ?? [];
                        setLocalAudioTracks(
                          settledTracks.length > 0 ? settledTracks : sourceAudioTracks
                        );
                        setFrameReadyId(danceId);
                        setHasPresentedLocalFrame(true);
                      }}
                      onQueuedItemAdopted={() => {
                        const danceId = current?.danceId ?? null;
                        const settledTracks = surfaceRef.current?.availableAudioTracks ?? [];
                        setLocalAudioTracks(
                          settledTracks.length > 0 ? settledTracks : sourceAudioTracks
                        );
                        setFrameReadyId(danceId);
                        setPresentingTimeId(danceId);
                        setHasPresentedLocalFrame(true);
                      }}
                      onTogglePlay={() => {
                        flushCurrentTelemetry();
                        setPlaying(!playingIntentRef.current);
                        pokeChrome();
                      }}
                      onPrevious={() => void goToPrevious()}
                      onNext={castConnected ? cast.next : () => void goToNext()}
                      hasPrevious={index > 0}
                      hasNext={index < queue.length - 1}
                      onAvailableAudioTracksChange={handleLocalAudioTracksChange}
                      onSelectedAudioTrackChange={(id) => {
                        // A null report is the expected HLS source-swap gap,
                        // not a user selection change. The authored default can
                        // also arrive before hls.js/AirPlay commits the durable
                        // preference; do not feed that stale report back into
                        // the preference prop and cancel the pending switch.
                        const accepted = acceptedReportedAudioFeedId(
                          id,
                          localPreferredAudioTrack
                        );
                        if (accepted) setLocalSelectedAudioTrack(accepted);
                      }}
                      onVideoRenditionChange={(danceId, rendition) => {
                        setReportedVideoRendition({ danceId, rendition });
                      }}
                      isFullscreen={isFullscreen}
                      onFullscreen={handleFullscreen}
                    />
                  )}
                  {airPlayActive && !ended ? (
                    <View
                      style={[
                        styles.castingPanel,
                        styles.airPlayPanel,
                        { pointerEvents: "none" }
                      ]}
                    >
                      {remotePlaybackContents}
                    </View>
                  ) : null}
                </>
              ) : (
                <View style={styles.rehearsal}>
                  <Text variant="title" color={palette.textSoft}>
                    Rehearsal mode
                  </Text>
                  <Muted variant="small" style={styles.rehearsalCopy}>
                    The synthetic fixture media is unavailable. Check your
                    connection and try again.
                  </Muted>
                </View>
              )}

              {!displacedPlayback ? (
                <PlayerVeil
                  uri={current ? mediaUrl(current.thumbPath) : recitalArtwork}
                  fallbackSeed={
                    current?.danceId ?? activePerformance?.performanceId ?? recital.recitalId
                  }
                  visible={veiled || exitCoverVisible}
                  danceId={current?.danceId ?? null}
                  showSpinner={!exitCoverVisible}
                />
              ) : null}

              {!displacedPlayback && playbackSession.authorizationStatus === "error" ? (
                <View style={styles.authorizationError}>
                  <Text variant="title" color={palette.text} style={styles.authorizationTitle}>
                    We couldn’t start playback
                  </Text>
                  <Muted variant="small" style={styles.authorizationCopy}>
                    Check your connection and try again.
                  </Muted>
                  <Button label="Try again" onPress={() => void retryQueue()} />
                </View>
              ) : null}

              {!displacedPlayback ? (
                <HeroCastPill
                  visible={!deviceDenied && playerTopChromeVisible}
                  compact={resolvedPlayerWidth < 560}
                  airPlayActive={
                    Platform.OS === "web" ? webAirPlayActive : airPlayActive
                  }
                  airPlayAvailable={
                    Platform.OS === "web" && webAirPlayAvailable
                  }
                  previewAirPlay={castPreviewActive}
                  onAirPlayPress={() => {
                    surfaceRef.current?.requestRemotePlayback?.();
                  }}
                  onAirPlayPickerOpened={handleAirPlayPickerOpened}
                  onAirPlayPickerClosed={handleAirPlayPickerClosed}
                  onInteractionStart={beginPlayerChromeInteraction}
                  onInteractionEnd={endPlayerChromeInteraction}
                  fullscreen={isFullscreen}
                />
              ) : null}

              {/* The top-left corner of every player surface: audience/clean
                  feed, one tap. Mirrors the route pill top-right on the same
                  chrome gutter; fades with the chrome during local playback.
                  In fullscreen the title is top-center, so the corner is ours. */}
              {!displacedPlayback && !deviceDenied && !ended ? (
                <View
                  pointerEvents="box-none"
                  style={[
                    styles.audioFeedChipWrap,
                    {
                      left:
                        (resolvedPlayerWidth < 560 ? space.lg : space.xl) +
                        (isFullscreen ? insets.left : 0),
                      top:
                        (resolvedPlayerWidth < 560 ? space.lg : space.xl) +
                        (isFullscreen ? insets.top : 0)
                    }
                  ]}
                >
                  <AudioFeedChip
                    tracks={remoteAudioTracks}
                    selectedId={remoteSelectedAudioTrack}
                    onSelect={
                      castPreviewActive ? setPreviewAudioTrack : handleSelectAudioTrack
                    }
                    onOpenSheet={() => setSettingsOpen(true)}
                    compact={resolvedPlayerWidth < 560}
                    visible={playerTopChromeVisible}
                    onInteractionStart={beginPlayerChromeInteraction}
                    onInteractionEnd={endPlayerChromeInteraction}
                  />
                </View>
              ) : null}

              {Platform.OS !== "web" && !displacedPlayback && !remotePlaybackActive && !deviceDenied && !playbackError && !ended ? (
                <Pressable
                  accessibilityLabel="Toggle playback controls"
                  onPress={toggleChrome}
                  style={StyleSheet.absoluteFill}
                />
              ) : null}

              {!displacedPlayback && !deviceDenied && playbackError ? (
                <View style={styles.errorOverlay}>
                  <Text variant="title" color={palette.text}>
                    Playback hit a snag.
                  </Text>
                  <Muted variant="small">{playbackError}</Muted>
                  <View style={styles.errorButtons}>
                    <Button
                      label="Try again"
                      onPress={() => {
                        setPlaybackError(null);
                        setLoadGeneration((value) => value + 1);
                        if (Platform.OS === "ios") airPlay.setPlaying(true);
                        else setPlaying(true);
                      }}
                    />
                    {index + 1 < queue.length ? (
                      <Button
                        label="Skip to next"
                        variant="secondary"
                        onPress={() => void goToNext()}
                      />
                    ) : null}
                  </View>
                </View>
              ) : !displacedPlayback && !deviceDenied && ended ? (
                <View style={styles.errorOverlay}>
                  <Backdrop
                    uri={resolvedThumbnailUrl ?? recitalArtwork}
                    fallbackSeed={
                      current?.danceId ?? activePerformance?.performanceId ?? recital.recitalId
                    }
                    intensity={1}
                  />
                  <RemotePlaybackTitleBlock
                    eyebrow="Recital complete"
                    title="That’s the show."
                  />
                  <View style={styles.errorButtons}>
                    <Button
                      label="Watch again"
                      onPress={() => {
                        // Starting over is the fresh slate: watched history
                        // clears so the performance's checkmarks reset too.
                        watchedRef.current.clear();
                        void goToWithLocalTransition(0);
                      }}
                    />
                    <Button
                      label="Back to recital"
                      variant="secondary"
                      onPress={handleBackToRecital}
                    />
                  </View>
                </View>
              ) : appChrome && !displacedPlayback && !deviceDenied ? (
                <PlayerChrome
                  dance={current}
                  playing={playerPlaying}
                  currentTime={playerCurrentTime}
                  duration={playerDuration || (current?.durationSeconds ?? 0)}
                  visible={!deviceDenied && (remotePlaybackActive || chromeVisible || !playerPlaying)}
                  hasPrevious={index > 0}
                  hasNext={index + 1 < queue.length}
                  onTogglePlay={
                    castConnected
                      ? () => {
                          flushCurrentTelemetry();
                          cast.togglePlaying();
                        }
                      : Platform.OS === "ios"
                        ? airPlay.togglePlaying
                        : () => {
                            flushCurrentTelemetry();
                            const nextPlaying = !playingIntentRef.current;
                            console.log(`[play] intent=${nextPlaying} source=user-tap`);
                            setPlaying(nextPlaying);
                          }
                  }
                  onSeek={
                    castConnected
                      ? (seconds) => {
                          flushCurrentTelemetry();
                          cast.seek(seconds);
                        }
                      : Platform.OS === "ios"
                        ? airPlay.seek
                        : (seconds) => {
                            flushCurrentTelemetry();
                            surfaceRef.current?.seek(seconds);
                          }
                  }
                  onScrubStart={() => {
                    flushCurrentTelemetry();
                    scrubbingRef.current = true;
                    playingBeforeScrubRef.current = playerPlaying;
                    if (Platform.OS !== "web") setScrollLockedForScrub(true);
                    if (!castConnected) surfaceRef.current?.beginScrubbing();
                  }}
                  onScrubEnd={(seconds) => {
                    if (!castConnected) {
                      console.log(`[play] scrubbing=end target=${seconds ?? "none"}`);
                      surfaceRef.current?.endScrubbing();
                      // The release position is authoritative. Allow the
                      // player's exact post-seek clock sample through while
                      // continuing to suppress provisional drag samples.
                      scrubbingRef.current = false;
                      if (seconds !== undefined) surfaceRef.current?.seek(seconds);
                    } else if (seconds !== undefined) {
                      cast.seek(seconds);
                      recordTelemetryPosition(seconds);
                    }
                    scrubbingRef.current = false;
                    if (!castConnected) {
                      if (Platform.OS === "ios") {
                        airPlay.setPlaying(playingBeforeScrubRef.current);
                      } else {
                        setPlaying(playingBeforeScrubRef.current);
                      }
                      if (playingBeforeScrubRef.current) {
                        surfaceRef.current?.playAfterScrubbing();
                      }
                    }
                    if (Platform.OS !== "web") setScrollLockedForScrub(false);
                  }}
                  onPrevious={() => void goToPrevious()}
                  onNext={
                    castConnected
                      ? () => {
                          cast.next();
                          if (index + 1 >= queue.length) handleEnded();
                        }
                      : () => void goToNext()
                  }
                  onPictureInPicture={
                    remotePlaybackActive ? undefined : handlePictureInPicture
                  }
                  onOpenSettings={
                    settingsAvailable && !remotePlaybackActive
                      ? () => setSettingsOpen(true)
                      : undefined
                  }
                  isFullscreen={isFullscreen}
                  onFullscreen={remotePlaybackActive ? undefined : handleFullscreen}
                  onExitFullscreen={handleExitFullscreen}
                  videoContentFit={fullscreenContentFit}
                  onToggleVideoContentFit={
                    isFullscreen && !remotePlaybackActive
                      ? toggleFullscreenContentFit
                      : undefined
                  }
                  onInteraction={pokeChrome}
                  compact={resolvedPlayerWidth < 560}
                  remotePlayback={remotePlaybackActive}
                  remoteEyebrow={
                    remotePlaybackDestination
                      ? `Playing on ${remotePlaybackDestination}`
                      : undefined
                  }
                  playbackRate={settingsRate}
                  progressSampleIntervalMs={castConnected ? 1000 : 500}
                  hideTitle={remotePlaybackActive}
                />
              ) : null}
                </PlayerInteractionSurface>
              ) : null}

              {deviceDenied || devicePanelMounted ? (
                <Animated.View
                  style={[
                    styles.deviceSwapLayer,
                    framelessDevicePanel && styles.deviceSwapLayerFrameless,
                    {
                      opacity: devicePanelOpacity,
                      pointerEvents: deviceDenied ? "auto" : "none"
                    }
                  ]}
                >
                  <DeviceSwapPanel
                    artworkUri={pageArtwork ?? null}
                    fallbackSeed={activePerformance?.performanceId ?? recital.recitalId}
                    framed={wide}
                    blocked={deviceDenied && !forceDevDeviceDenied}
                    forcePreview={forceDevDeviceDenied}
                    previewState={swapPreviewState}
                    onPreviewResolve={() => router.setParams({ deviceDenied: "" })}
                  />
                </Animated.View>
              ) : null}

              {!framelessDevicePanel ? (
                <View style={[styles.playerFrameBorder, { pointerEvents: "none" }]} />
              ) : null}

              {Platform.OS === "web" && playbackStatsVisible ? (
                <View
                  accessibilityRole="summary"
                  style={[styles.playbackStats, { pointerEvents: "none" }]}
                >
                  <Text variant="small" color="#FFFFFF" style={styles.playbackStatsTitle}>
                    Playback stats
                  </Text>
                  <Text variant="small" color="#FFFFFF" style={styles.playbackStatsLine}>
                    {`Route  ${castConnected ? "Google Cast" : webAirPlayActive ? "AirPlay" : "This browser"}`}
                  </Text>
                  <Text variant="small" color="#FFFFFF" style={styles.playbackStatsLine}>
                    {castConnected || webAirPlayActive
                      ? "Quality  Receiver controlled · adaptive"
                      : `Active  ${activeVideoRendition?.width ?? "—"}×${activeVideoRendition?.height ?? "—"} · ${playbackStatsCodecLabel(activeVideoRendition?.codec, surfaceDiagnostics?.active?.playbackEngine, "receiver controlled")}`}
                  </Text>
                  {!castConnected && !webAirPlayActive ? (
                    <>
                      <Text variant="small" color="#FFFFFF" style={styles.playbackStatsLine}>
                        {`Stream  ${surfaceDiagnostics?.active?.advertisedBitrateBps ? `${(surfaceDiagnostics.active.advertisedBitrateBps / 1_000_000).toFixed(1)} Mb/s` : "—"} · connection ${(surfaceDiagnostics?.active?.bandwidthEstimateBps ?? 0) > 0 ? `${(surfaceDiagnostics!.active!.bandwidthEstimateBps! / 1_000_000).toFixed(1)} Mb/s` : "—"}`}
                      </Text>
                      <Text variant="small" color="#FFFFFF" style={styles.playbackStatsLine}>
                        {`Buffer  ${surfaceDiagnostics?.active ? surfaceDiagnostics.active.bufferAheadSeconds.toFixed(1) : "—"}s`}
                      </Text>
                      <Text variant="small" color="#FFFFFF" style={styles.playbackStatsLine}>
                        {`State  ${surfaceDiagnostics?.active?.paused ? "paused" : "playing"} · t=${surfaceDiagnostics?.active ? surfaceDiagnostics.active.currentTimeSeconds.toFixed(1) : "—"}s · ready=${surfaceDiagnostics?.active?.readyState ?? "—"} · error=${surfaceDiagnostics?.active?.mediaErrorCode ?? "—"}`}
                      </Text>
                      <Text variant="small" color="#FFFFFF" style={styles.playbackStatsLine}>
                        {`Next  ${surfaceDiagnostics?.standby?.rendition?.width ?? "—"}×${surfaceDiagnostics?.standby?.rendition?.height ?? "—"} · ${playbackStatsCodecLabel(surfaceDiagnostics?.standby?.rendition?.codec, surfaceDiagnostics?.standby?.rendition ? surfaceDiagnostics.standby.playbackEngine : undefined, "preparing")}`}
                      </Text>
                    </>
                  ) : null}
                  <Text variant="small" color="#FFFFFF" style={styles.playbackStatsHint}>
                    Control+Option+Shift+D to close
                  </Text>
                </View>
              ) : null}

              <PlaybackSettingsSheet
                visible={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                audioTracks={settingsAudioTracks}
                selectedAudioTrack={settingsSelectedTrack}
                onSelectAudioTrack={handleSelectAudioTrack}
                playbackRate={settingsRate}
                onSelectPlaybackRate={handleSelectPlaybackRate}
              />
            </View>

            {/* In the wide rail layout the bar stays pinned with the player;
                stacked layouts scroll it with everything below the video. */}
            {wide ? nowPlayingBar : null}
          </View>

          {!isFullscreen ? (
            wide ? (
              <View style={styles.rail}>
                {queueList}
              </View>
            ) : (
              queueList
            )
          ) : null}
          </Animated.View>
        </View>
      </HeaderContentMask>
    </>
  );

  return (
    <View style={styles.screen}>
      <Stack.Screen
        options={{
          autoHideHomeIndicator: isFullscreen,
          navigationBarHidden: isFullscreen,
          orientation: isFullscreen ? "landscape" : "portrait",
          statusBarHidden: isFullscreen
        }}
      />
      {Platform.OS !== "web" ? (
        <View style={[styles.nativeTransitionSurface, { pointerEvents: "none" }]}>
          <PageBackdrop
            focalY={pageBackdrop.focalY}
            sourceUri={Platform.OS === "ios" ? pageArtwork : undefined}
          />
        </View>
      ) : null}
      {Platform.OS === "web" ? (
        <div
          ref={webStageScrollRef}
          onPointerDownCapture={
            webStageQueue ? cancelQueueScrollAnimation : undefined
          }
          onWheel={webStageQueue ? cancelQueueScrollAnimation : undefined}
          onScroll={webStageQueue ? handleWebStageScroll : undefined}
          style={{
            display: "flex",
            flex: "1 1 0%",
            flexDirection: "column",
            minHeight: 0,
            overflowX: "hidden",
            overflowY:
              isFullscreen || (webStageQueue && !scrollLockedForScrub)
                ? "auto"
                : "hidden",
            overscrollBehaviorY: webStageQueue ? "contain" : "auto",
            scrollbarWidth: "none",
            WebkitOverflowScrolling: "touch"
          }}
        >
          <View
            style={
              webStageQueue
                ? [styles.webStickyViewport, { height }]
                : styles.screen
            }
          >
            {watchViewport}
          </View>
          {webStageScrollDistance > 0 ? (
            <View style={{ height: webStageScrollDistance }} />
          ) : null}
        </div>
      ) : (
        watchViewport
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  audioFeedChipWrap: {
    position: "absolute",
    zIndex: 40
  },
  screen: {
    flex: 1,
    minHeight: 0
  },
  webStageScrollContent: {
    minHeight: "100%"
  },
  // The visible stage stays physically pinned while the full-viewport
  // ScrollView supplies native wheel momentum and boundary overscroll. Because
  // every control is inside this sticky descendant, wheel input anywhere on
  // the stage naturally bubbles to the same scroll owner.
  webStickyViewport: {
    flexShrink: 0,
    minHeight: 0,
    position: "sticky",
    top: 0,
    width: "100%",
    zIndex: 1
  } as ViewStyle,
  nativeTransitionSurface: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: palette.bg
  },
  stageFrame: {
    alignItems: "center",
    flex: 1,
    minHeight: 0,
    overflow: "hidden"
  },
  stageFrameFullscreen: {
    alignItems: "stretch",
    backgroundColor: "#000",
    paddingTop: 0
  },
  fullscreenHidden: {
    display: "none"
  },
  headerWrap: {
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 2
  },
  headerBackdrop: {
    bottom: 0,
    left: 0,
    overflow: "hidden",
    position: "absolute",
    right: 0,
    top: 0
  },
  headerContent: {
    // Keep navigation aligned with the full playback stage below it.
    alignSelf: "center",
    maxWidth: PLAYBACK_MAX_WIDTH,
    width: "100%"
  },
  emptyWrap: {
    alignItems: "center",
    flex: 1,
    gap: space.lg,
    justifyContent: "center"
  },
  stage: {
    flex: 1,
    maxWidth: PLAYBACK_MAX_WIDTH,
    minHeight: 0,
    paddingHorizontal: space.xl,
    width: "100%"
  },
  stageWide: {
    flexDirection: "row",
    gap: space.xl,
    justifyContent: "center"
  },
  stageFullscreen: {
    flex: 1,
    flexDirection: "column",
    gap: 0,
    maxWidth: undefined,
    paddingHorizontal: 0,
    width: "100%"
  },
  playerColumn: {
    width: "100%"
  },
  // The web queue is translated beneath the pinned stage as the page scrolls.
  // Keep the complete player hit plane above that transformed stacking
  // context so an iPad tap can never land on a dance row behind the controls.
  webPlayerForeground: {
    position: "relative",
    zIndex: 2
  },
  playerColumnWide: {
    flex: 1,
    minWidth: 0
  },
  playerColumnCentered: {
    alignSelf: "center"
  },
  playerColumnFullscreen: {
    backgroundColor: "#000",
    flex: 1,
    minWidth: 0,
    width: "100%"
  },
  playerBox: {
    backgroundColor: palette.playerBg,
    borderRadius: radius.lg,
    overflow: "hidden",
    width: "100%"
  },
  playerBoxFrameless: {
    backgroundColor: "transparent"
  },
  playerBoxVideo: {
    aspectRatio: 16 / 9
  },
  playerBoxFullscreen: {
    borderRadius: 0,
    flex: 1,
    width: "100%"
  },
  playerBoxDeviceSwap: {
    justifyContent: "center"
  },
  playerInteractive: {
    flex: 1
  },
  playerInteractiveBehindSwap: {
    ...StyleSheet.absoluteFillObject
  },
  playerFrameBorder: {
    ...StyleSheet.absoluteFillObject,
    borderColor: palette.hairline,
    borderRadius: radius.lg,
    borderWidth: 1,
    // Keep this transparent full-frame overlay on zIndex only. Elevating it
    // creates a stale rectangular compositor tile on Samsung/Android 16.
    zIndex: 50
  },
  playbackStats: {
    backgroundColor: "rgba(6, 8, 13, 0.88)",
    borderColor: "rgba(255, 255, 255, 0.2)",
    borderRadius: radius.sm,
    borderWidth: 1,
    bottom: space.md,
    gap: 2,
    left: space.md,
    maxWidth: 390,
    paddingHorizontal: space.md,
    paddingVertical: space.sm,
    position: "absolute",
    zIndex: 80
  },
  playbackStatsTitle: {
    fontFamily: fonts.bodySemiBold,
    marginBottom: 2
  },
  playbackStatsLine: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 17
  },
  playbackStatsHint: {
    color: "rgba(255, 255, 255, 0.58)",
    fontFamily: fonts.body,
    fontSize: 11,
    lineHeight: 16,
    marginTop: 2
  },
  castingPanel: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
    overflow: "hidden"
  },
  airPlayPanel: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1
  },
  rehearsal: {
    alignItems: "center",
    flex: 1,
    gap: space.sm,
    justifyContent: "center",
    padding: space.xl
  },
  rehearsalMark: {
    color: palette.faint,
    fontSize: 18
  },
  rehearsalCopy: {
    maxWidth: 360,
    textAlign: "center"
  },
  errorOverlay: {
    alignItems: "center",
    backgroundColor: palette.scrim,
    bottom: 0,
    gap: space.md,
    justifyContent: "center",
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
    elevation: 35,
    zIndex: 35
  },
  deviceSwapLayer: {
    backgroundColor: palette.bg,
    flexGrow: 1,
    overflow: "hidden",
    width: "100%",
    elevation: 40,
    zIndex: 40
  },
  deviceSwapLayerFrameless: {
    backgroundColor: "transparent"
  },
  authorizationError: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    backgroundColor: palette.surfaceSunken,
    borderColor: palette.hairlineStrong,
    borderRadius: radius.lg,
    borderWidth: 1,
    elevation: 40,
    gap: space.md,
    justifyContent: "center",
    margin: space.xl,
    padding: space.xl,
    zIndex: 40
  },
  authorizationTitle: {
    textAlign: "center"
  },
  authorizationCopy: {
    maxWidth: 360,
    textAlign: "center"
  },
  errorButtons: {
    flexDirection: "row",
    gap: space.md,
    marginTop: space.sm
  },
  nowPlaying: {
    alignItems: "center",
    borderColor: palette.hairline,
    borderRadius: radius.lg,
    borderWidth: 1,
    flexDirection: "row",
    gap: space.xl,
    justifyContent: "space-between",
    overflow: "hidden",
    padding: space.lg
  },
  nowPlayingWide: {
    marginTop: space.xl
  },
  nowPlayingCollapsed: {
    marginTop: 10
  },
  nowPlayingStacked: {
    alignItems: "stretch",
    flexDirection: "column",
    gap: space.md
  },
  // Thumb + text ride together in both orientations; only the up-next
  // block reflows when the bar stacks.
  nowPlayingMain: {
    alignItems: "center",
    flex: 1,
    flexDirection: "row",
    gap: space.lg,
    minWidth: 0
  },
  // A touch taller than the text block — the bar's hero, sized to read as
  // kin to the list rows' film frames rather than an inline chip.
  nowPlayingThumbWrap: {
    aspectRatio: 16 / 9,
    flexShrink: 0,
    position: "relative",
    width: 120
  },
  nowPlayingThumb: {
    borderRadius: radius.sm,
    bottom: 0,
    left: 0,
    position: "absolute",
    right: 0,
    top: 0
  },
  // Same sliver grammar as DanceRow's thumbnails.
  nowPlayingThumbProgressClip: {
    borderBottomLeftRadius: radius.sm,
    borderBottomRightRadius: radius.sm,
    bottom: 0,
    left: 0,
    overflow: "hidden",
    position: "absolute",
    right: 0
  },
  nowPlayingThumbProgressTrack: {
    backgroundColor: "rgba(243, 244, 250, 0.22)",
    height: 4
  },
  nowPlayingThumbProgressFillClip: {
    height: 4,
    overflow: "hidden"
  },
  nowPlayingThumbProgressFill: {
    height: 4,
    width: "100%"
  },
  nowPlayingText: {
    flex: 1,
    gap: 3,
    minWidth: 0
  },
  nowTitle: {
    fontFamily: fonts.display
  },
  upNext: {
    alignSelf: "stretch",
    backgroundColor: "rgba(13, 14, 20, 0.25)",
    borderRadius: radius.md,
    borderWidth: 0,
    flexShrink: 0,
    gap: 2,
    justifyContent: "center",
    paddingHorizontal: space.lg + 1,
    paddingVertical: space.md + 1,
    position: "relative",
    width: 220
  },
  upNextRow: {
    alignItems: "center",
    borderTopColor: palette.hairline,
    borderTopWidth: 1,
    flexDirection: "row",
    gap: space.xs,
    paddingTop: space.md
  },
  upNextRowTitle: {
    flexShrink: 1,
    minWidth: 0
  },
  upNextRowCountdown: {
    flexShrink: 0
  },
  upNextRowHovered: {
    borderTopColor: palette.hairlineStrong
  },
  upNextHovered: {
    backgroundColor: "rgba(13, 14, 20, 0.35)"
  },
  rail: {
    maxWidth: 400,
    minHeight: 0,
    width: "36%"
  },
  queuePanel: {
    flex: 1,
    marginTop: space.xl,
    minHeight: 0
  },
  queuePanelWide: {
    marginTop: space.lg
  },
  queueScroll: {
    flex: 1,
    minHeight: 0,
    zIndex: 1
  },
  queueScrollBody: {
    gap: space.md,
    paddingHorizontal: space.md,
    paddingTop: 15
  },
  // DanceRow deliberately bleeds its lit surface 12px past its text/thumb
  // alignment. A CSS/RN mask clips to its own border box, so expand that box
  // and restore the content gutter inside the ScrollView. The row surface can
  // now reach its intended edge without Chrome amputating it.
  queueFadeBleed: {
    marginHorizontal: -space.md,
    position: "relative"
  },
  stackedFlow: {
    marginHorizontal: -space.md,
    marginTop: 0
  },
  stackedFlowBody: {
    gap: space.md,
    paddingHorizontal: space.md,
    paddingTop: space.md
  },
  stackedListHeader: {
    marginTop: space.md
  },
  queueEmpty: {
    paddingVertical: space.lg,
    textAlign: "center"
  }
});
